import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { useCurrencyMaster } from '../../../hooks/useCurrencyMaster';
import '../../../assets/styles/po_creation.scss';

export default function POCreation() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Master Data Hooks
  const { attributeUnitOptions } = useProductDetailsMaster();
  const { shippingTermsOptions, paymentScheduleOptions, loading: incotermLoading } = useFactoryDetailsMaster();
  const { currencyOptions, loading: currencyLoading } = useCurrencyMaster();
  
  const [loading, setLoading] = useState(true);
  const [quotationDataState, setQuotationDataState] = useState(null);
  const [rfqDataState, setRfqDataState] = useState(null);
  const [buyerDetailsState, setBuyerDetailsState] = useState(null);
  const [sellerDetailsState, setSellerDetailsState] = useState(null);
  
  const [formData, setFormData] = useState({
    poNumber: '',
    poDate: new Date().toISOString().split('T')[0],
    expectedDeliveryDate: '',
    priorityLevel: 'Normal',
    paymentTerms: '',
    currency: '',
    freightTerms: '',
    deliveryTerms: '',
    tradeScenario: 'IN Intra-State (Same State in India)',
    tradeType: 'Intra-State Trade (CGST + SGST)',
    tradeLocation: 'Karnataka -> Karnataka',
    portOfLoading: '',
    portOfDischarge: '',
    customsBroker: '',
    hsCode: '',
    countryOfOrigin: '',
    incoterms: '',
    requiredDocuments: '',
    exportType: 'LUT (Letter of Undertaking) - 0% IGST',
    lutArn: '',
    buyerAddress: 'Plot 45, Industrial Area, Phase-2, Bangalore, Karnataka - 560058',
    buyerGstNumber: '29AABCT1332L1Z5',
    buyerState: 'Karnataka',
    buyerCountry: 'India',
    sellerAddress: '89, Electronic City, Bangalore, Karnataka - 560100',
    sellerGstNumber: '29BBCDE5678M1Z3',
    sellerState: 'Karnataka',
    sellerCountry: 'India'
  });

  const [lineItems, setLineItems] = useState([
    {
      id: 1,
      description: 'Industrial Grade Electrical Components',
      hsn: '8517',
      quantity: 100,
      uom: 'pcs',
      unitPrice: 1200,
      discount: 0,
      cgst: 9,
      sgst: 9,
      igst: 18,
      customsDuty: 10,
      cess: 0,
      tax: 0,
      total: 170600.11
    },
    {
      id: 2,
      description: 'Control Panel Assembly',
      hsn: '8537',
      quantity: 25,
      uom: 'units',
      unitPrice: 5000,
      discount: 0,
      cgst: 9,
      sgst: 9,
      igst: 18,
      customsDuty: 10,
      cess: 0,
      tax: 0,
      total: 177708.45
    }
  ]);

  const [deliverySchedules, setDeliverySchedules] = useState([]);

  const [termsConditions, setTermsConditions] = useState([
    { id: 1, text: 'All items must be BIS certified and meet the specifications mentioned in the RFQ.' },
    { id: 2, text: 'Delivery to be completed as per the schedule mentioned. Late delivery penalties apply.' },
    { id: 3, text: 'Payment will be released within 30 days of delivery and invoice submission.' },
    { id: 4, text: 'Supplier must provide installation support and training if required.' },
    { id: 5, text: 'Quality inspection will be conducted upon delivery. Rejection criteria as per industry standards.' }
  ]);

  const [cifConfig, setCifConfig] = useState({
    freightPercent: 5,
    insurancePercent: 1.5,
    handlingFixed: 5000
  });

  const [internationalCharges, setInternationalCharges] = useState({
    shippingCharges: 12250,
    insuranceCharges: 3675,
    handlingCharges: 5000
  });

  const [specialInstructions, setSpecialInstructions] = useState('Please ensure all items are packed securely. Provide advance notice 48 hours before dispatch. Include packing list and test certificates with shipment.');

  const [attachments, setAttachments] = useState([]);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const countryOptions = [
    'United States',
    'China',
    'Japan',
    'Germany',
    'United Kingdom',
    'France',
    'India',
    'South Korea',
    'Italy',
    'Canada',
    'Australia',
    'Brazil',
    'Mexico',
    'Spain',
    'Netherlands',
    'Russia',
    'Switzerland',
    'Belgium',
    'Sweden',
    'Poland',
    'Other'
  ];

  const stateOptions = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Other'
  ];

  // Generate PO Number (similar to quotation number generation)
  const generatePONumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    
    // Generate a unique 6-digit number from timestamp
    // Use the last 6 digits of the timestamp (milliseconds since epoch)
    // This ensures uniqueness while keeping the format readable
    const timestamp = now.getTime();
    const sequence = String(timestamp).slice(-6).padStart(6, '0');
    
    return `PO-${year}-${sequence}`;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to get unit label from value (ID or label)
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    
    // If master data not loaded yet, return the value as is
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) {
      return unitValue;
    }
    
    // First try to find by value (ID) - convert both to numbers for comparison
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      const optValueNum = isNaN(opt.value) ? null : Number(opt.value);
      // Try exact string match first
      if (String(opt.value) === String(unitValue)) return true;
      // Try numeric match if both are numbers
      if (unitValueNum !== null && optValueNum !== null) {
        return unitValueNum === optValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    // If not found by ID, try to find by label (might already be a label)
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    // If still not found, return the value as is
    return unitValue;
  };

  // Get quotation data from state or use defaults
  const quotationData = quotationDataState ? {
    quotationId: quotationDataState.quotation_number || `QUO-${quotationDataState.quotation_id}`,
    linkedRFQ: rfqDataState ? (rfqDataState.rfq_number || `RFQ-${rfqDataState.rfq_id}`) : 'N/A',
    quoteDate: formatDate(quotationDataState.quote_date || quotationDataState.created_at),
    validUntil: (() => {
      // Calculate valid until by adding validity_days to quote_date
      if (quotationDataState.quote_date && quotationDataState.validity_days) {
        const quoteDateObj = new Date(quotationDataState.quote_date);
        quoteDateObj.setDate(quoteDateObj.getDate() + parseInt(quotationDataState.validity_days));
        return formatDate(quoteDateObj);
      }
      return 'N/A';
    })(),
    sellerCompany: quotationDataState.seller_company_name || '',
    contactPerson: quotationDataState.seller_name || '',
    contactEmail: quotationDataState.seller_email || '',
    contactPhone: quotationDataState.seller_phone || ''
  } : {
    quotationId: 'N/A',
    linkedRFQ: 'N/A',
    quoteDate: 'N/A',
    validUntil: 'N/A',
    sellerCompany: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: ''
  };

  // Get buyer details from state or use defaults
  const buyerDetails = buyerDetailsState || {
    companyName: '',
    address: '',
    gstNumber: '',
    state: '',
    country: 'India',
    contactPerson: '',
    email: '',
    phone: ''
  };

  // Get seller details from state or use defaults
  const sellerDetails = sellerDetailsState || {
    companyName: '',
    address: '',
    gstNumber: '',
    state: '',
    country: 'India',
    contactPerson: '',
    email: '',
    phone: ''
  };

  // Helper function to check if trade scenario is international
  const isInternationalTrade = (scenario) => {
    return scenario === '→IN Import to India (Abroad → India)' ||
           scenario === 'IN→ Export from India (India → Abroad)' ||
           scenario === 'Third Country (Abroad → Abroad)';
  };

  // Helper function to get payment term label
  const getPaymentTermLabel = (paymentTermValue) => {
    // Check if value is empty, null, undefined, or just whitespace
    if (!paymentTermValue || (typeof paymentTermValue === 'string' && paymentTermValue.trim() === '')) {
      return 'Not specified';
    }
    
    // If paymentScheduleOptions is available, try to find a match
    if (paymentScheduleOptions && paymentScheduleOptions.length > 0) {
      // Try exact match on label
      let found = paymentScheduleOptions.find(opt => 
        opt.label === paymentTermValue
      );
      
      // Try exact match on value
      if (!found) {
        found = paymentScheduleOptions.find(opt => 
          opt.value === paymentTermValue || String(opt.value) === String(paymentTermValue)
        );
      }
      
      // Try case-insensitive match on label
      if (!found) {
        found = paymentScheduleOptions.find(opt => 
          opt.label && opt.label.toLowerCase() === String(paymentTermValue).toLowerCase()
        );
      }
      
      // Try partial match on label
      if (!found) {
        found = paymentScheduleOptions.find(opt => 
          opt.label && opt.label.toLowerCase().includes(String(paymentTermValue).toLowerCase())
        );
      }
      
      if (found) {
        return found.label;
      }
    }
    
    // Fallback: return the value as-is
    return paymentTermValue;
  };

  // Helper function to get delivery term label
  const getDeliveryTermLabel = (deliveryTermValue) => {
    // Check if value is empty, null, undefined, or just whitespace
    if (!deliveryTermValue || (typeof deliveryTermValue === 'string' && deliveryTermValue.trim() === '')) {
      return 'Not specified';
    }
    
    // Valid delivery term options
    const validDeliveryTerms = ['Ex-Works', 'Door Delivery', 'Port Delivery', 'Warehouse Pickup'];
    
    // Try exact match (case-insensitive)
    const matched = validDeliveryTerms.find(term => 
      term.toLowerCase() === String(deliveryTermValue).toLowerCase()
    );
    
    if (matched) {
      return matched;
    }
    
    // Fallback: return the value as-is
    return deliveryTermValue;
  };

  // Function to determine trade scenario based on buyer and seller locations
  const determineTradeScenario = (buyerCountry, buyerState, sellerCountry, sellerState) => {
    const buyerIsIndia = buyerCountry && buyerCountry.toLowerCase() === 'india';
    const sellerIsIndia = sellerCountry && sellerCountry.toLowerCase() === 'india';
    
    // Both in India
    if (buyerIsIndia && sellerIsIndia) {
      // Same state - Intra-State
      if (buyerState && sellerState && buyerState === sellerState) {
        return {
          tradeScenario: 'IN Intra-State (Same State in India)',
          tradeType: 'Intra-State Trade (CGST + SGST)',
          tradeLocation: `${buyerState} -> ${sellerState}`
        };
      } else {
        // Different states - Inter-State
        return {
          tradeScenario: 'IN Inter-State (Between Indian States)',
          tradeType: 'Inter-State Trade (IGST)',
          tradeLocation: `${buyerState || 'India'} ↔ ${sellerState || 'India'}`
        };
      }
    }
    
    // Import to India (Seller abroad, Buyer in India)
    if (sellerIsIndia === false && buyerIsIndia) {
      return {
        tradeScenario: '→IN Import to India (Abroad → India)',
        tradeType: 'Import: CIF + BCD + SWS + IGST',
        tradeLocation: `${sellerCountry || 'Abroad'} → India`
      };
    }
    
    // Export from India (Seller in India, Buyer abroad)
    if (sellerIsIndia && buyerIsIndia === false) {
      return {
        tradeScenario: 'IN→ Export from India (India → Abroad)',
        tradeType: 'Export: 0% IGST (with LUT)',
        tradeLocation: `India → ${buyerCountry || 'Abroad'}`
      };
    }
    
    // Third Country (Both abroad)
    if (buyerIsIndia === false && sellerIsIndia === false) {
      return {
        tradeScenario: 'Third Country (Abroad → Abroad)',
        tradeType: 'Third Country Trade',
        tradeLocation: `${sellerCountry || 'Abroad'} → ${buyerCountry || 'Abroad'}`
      };
    }
    
    // Default fallback
    return {
      tradeScenario: 'IN Intra-State (Same State in India)',
      tradeType: 'Intra-State Trade (CGST + SGST)',
      tradeLocation: 'India -> India'
    };
  };

  // Fetch quotation and related data
  useEffect(() => {
    // Generate PO number on component mount if not already set
    setFormData(prev => ({
      ...prev,
      poNumber: prev.poNumber || generatePONumber(),
      poDate: prev.poDate || new Date().toISOString().split('T')[0]
    }));

    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Check if editing an existing draft PO
        const poIdFromState = location.state?.poId || location.state?.purchaseOrderId;
        let poId = null; // Declare outside try block for error logging
        if (poIdFromState) {
          try {
            // Ensure poId is a number (extract from formatted string if needed)
            poId = poIdFromState;
            if (typeof poId === 'string' && poId.startsWith('PO-')) {
              // Extract numeric ID from PO-YYYY-XXXXXX format
              const parts = poId.split('-');
              if (parts.length >= 3) {
                poId = parseInt(parts[2]);
              }
            } else if (typeof poId === 'string') {
              poId = parseInt(poId);
            }
            
            if (isNaN(poId)) {
              throw new Error('Invalid purchase order ID');
            }
            
            const poResponse = await CommonService.get(`/purchase-order/${poId}`);
            if (poResponse && poResponse.success && poResponse.data) {
              const existingPO = poResponse.data;
              
              // Load existing PO data into form
              // Support both po_data structure and direct fields
              const poData = existingPO.po_data || existingPO;
                setFormData(prev => ({
                  ...prev,
                  poNumber: existingPO.po_number || prev.poNumber,
                  poDate: (() => {
                    // Format po_date to YYYY-MM-DD format for date input
                    // Check multiple sources: existingPO.po_date, poData.poDate, existingPO.poDate
                    const poDateValue = existingPO.po_date || poData.poDate || existingPO.poDate || prev.poDate;
                    if (poDateValue) {
                      // If it's already a string in YYYY-MM-DD format, use it
                      if (typeof poDateValue === 'string' && poDateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        return poDateValue;
                      }
                      // If it's a Date object or other format, convert it
                      try {
                        const date = new Date(poDateValue);
                        if (!isNaN(date.getTime())) {
                          return date.toISOString().split('T')[0];
                        }
                      } catch (e) {
                        // If parsing fails, return the original value
                      }
                    }
                    return poDateValue || prev.poDate;
                  })(),
                  expectedDeliveryDate: (() => {
                    // Format expected_delivery_date to YYYY-MM-DD format for date input
                    const expectedDateValue = existingPO.expected_delivery_date || poData.expectedDeliveryDate || prev.expectedDeliveryDate;
                    if (expectedDateValue) {
                      // If it's already a string in YYYY-MM-DD format, use it
                      if (typeof expectedDateValue === 'string' && expectedDateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                        return expectedDateValue;
                      }
                      // If it's a Date object or other format, convert it
                      try {
                        const date = new Date(expectedDateValue);
                        if (!isNaN(date.getTime())) {
                          return date.toISOString().split('T')[0];
                        }
                      } catch (e) {
                        // If parsing fails, return the original value
                      }
                    }
                    return expectedDateValue || prev.expectedDeliveryDate;
                  })(),
                  priorityLevel: poData.priorityLevel || existingPO.priority_level || prev.priorityLevel,
                  paymentTerms: poData.paymentTerms || existingPO.payment_terms || prev.paymentTerms,
                  currency: poData.currency || existingPO.currency || prev.currency,
                  freightTerms: poData.freightTerms || existingPO.freight_terms || existingPO.incoterms || prev.freightTerms,
                  deliveryTerms: poData.deliveryTerms || existingPO.delivery_terms || prev.deliveryTerms,
                  tradeScenario: poData.tradeScenario || existingPO.trade_scenario || prev.tradeScenario,
                  tradeType: poData.tradeType || existingPO.trade_type || prev.tradeType,
                  tradeLocation: poData.tradeLocation || existingPO.trade_location || prev.tradeLocation,
                  buyerAddress: (() => {
                    // Handle buyer address - it might be a JSON string that needs parsing
                    let address = poData.buyerAddress || existingPO.buyer_address || prev.buyerAddress;
                    if (address && typeof address === 'string') {
                      // Check if it's a JSON string
                      if (address.trim().startsWith('{')) {
                        try {
                          const addressData = JSON.parse(address);
                          if (addressData && typeof addressData === 'object') {
                            // Extract the address field from JSON object
                            address = addressData.address || addressData.address_line1 || address;
                          }
                        } catch (e) {
                          // Not valid JSON, use as is
                        }
                      }
                    }
                    return address;
                  })(),
                  buyerGstNumber: poData.buyerGstNumber || existingPO.buyer_gst_number || prev.buyerGstNumber,
                  buyerCountry: poData.buyerCountry || existingPO.buyer_country || prev.buyerCountry,
                  buyerState: poData.buyerState || existingPO.buyer_state || prev.buyerState,
                  sellerAddress: poData.sellerAddress || existingPO.seller_address || prev.sellerAddress,
                  sellerGstNumber: poData.sellerGstNumber || existingPO.seller_gst_number || prev.sellerGstNumber,
                  sellerCountry: poData.sellerCountry || existingPO.seller_country || prev.sellerCountry,
                  sellerState: poData.sellerState || existingPO.seller_state || prev.sellerState,
                  portOfLoading: poData.portOfLoading || existingPO.port_of_loading || prev.portOfLoading,
                  portOfDischarge: poData.portOfDischarge || existingPO.port_of_discharge || prev.portOfDischarge,
                  customsBroker: poData.customsBroker || existingPO.customs_broker || prev.customsBroker,
                  hsCode: poData.hsCode || existingPO.hs_code || prev.hsCode,
                  countryOfOrigin: poData.countryOfOrigin || existingPO.country_of_origin || prev.countryOfOrigin,
                  incoterms: poData.incoterms || existingPO.incoterms || prev.incoterms,
                  exportType: poData.exportType || existingPO.export_type || prev.exportType,
                  lutArn: poData.lutArn || existingPO.lut_arn || prev.lutArn
                }));
                
                // Load line items (support both po_data.lineItems and line_items)
                const lineItemsData = poData.lineItems || existingPO.line_items || [];
                if (lineItemsData && lineItemsData.length > 0) {
                  setLineItems(lineItemsData);
                }
                
                // Load delivery schedules (support both po_data.deliverySchedules and delivery_schedules)
                const deliverySchedulesData = poData.deliverySchedules || existingPO.delivery_schedules || [];
                if (deliverySchedulesData && deliverySchedulesData.length > 0) {
                  setDeliverySchedules(deliverySchedulesData);
                }
                
                // Load terms conditions (support both po_data.termsConditions and terms_conditions)
                const termsConditionsData = poData.termsConditions || existingPO.terms_conditions || [];
                if (termsConditionsData && termsConditionsData.length > 0) {
                  setTermsConditions(termsConditionsData);
                }
                
                // Load CIF config
                const cifConfigData = poData.cifConfig || existingPO.cif_config;
                if (cifConfigData) {
                  setCifConfig(cifConfigData);
                }
                
                // Load special instructions
                const specialInstructionsData = poData.specialInstructions || existingPO.special_instructions;
                if (specialInstructionsData) {
                  setSpecialInstructions(specialInstructionsData);
                }
                
                // Load attachments (support both po_data.attachments and attachments)
                const attachmentsData = poData.attachments || existingPO.attachments || [];
                if (attachmentsData && attachmentsData.length > 0) {
                  setAttachments(attachmentsData);
                }
              
              // Fetch quotation details for reference
              if (existingPO.quotation_id) {
                try {
                  const quotationResponse = await CommonService.get(`/quotation/${existingPO.quotation_id}`);
                  if (quotationResponse && quotationResponse.success && quotationResponse.data) {
                    setQuotationDataState(quotationResponse.data);
                  }
                } catch (quoteError) {
                  console.warn('Error fetching quotation for existing PO:', quoteError);
                }
              }
              
              setLoading(false);
              return; // Exit early since we're editing an existing PO
            }
          } catch (poError) {
            console.error('Error fetching existing purchase order:', poError);
            console.error('PO ID attempted:', poId);
            console.error('Error details:', poError?.response?.data || poError?.message);
            const errorMessage = poError?.response?.data?.message || poError?.message || 'Failed to load purchase order data';
            toast.error(errorMessage);
            navigate('/purchase-order-list');
            return;
          }
        }
        
        // Get quotationId from location state (for new PO creation)
        const quotationIdFromState = location.state?.quotationId;
        const quotationFromState = location.state?.quotation;
        const rfqFromState = location.state?.rfq;
        
        let quotationId = quotationIdFromState;
        
        // If quotationId is not in state, try to get it from quotation object
        if (!quotationId && quotationFromState) {
          quotationId = quotationFromState.quotation_id;
        }
        
        if (!quotationId) {
          toast.error('Quotation ID not found. Please select a quotation first.');
          navigate('/purchase-order-list');
          return;
        }
        
        // Fetch quotation details
        let quotation = quotationFromState;
        if (!quotation) {
          try {
            const quotationResponse = await CommonService.get(`/quotation/${quotationId}`);
            if (quotationResponse && quotationResponse.success && quotationResponse.data) {
              quotation = quotationResponse.data;
            }
          } catch (quoteError) {
            console.error('Error fetching quotation:', quoteError);
            toast.error('Failed to fetch quotation details');
            navigate('/purchase-order-list');
            return;
          }
        }
        
        if (!quotation) {
          toast.error('Quotation not found');
          navigate('/purchase-order-list');
          return;
        }
        
        setQuotationDataState(quotation);
        
        // Fetch RFQ details
        let rfq = rfqFromState;
        if (!rfq && quotation.rfq_id) {
          try {
            const rfqResponse = await CommonService.get(`/rfq/${quotation.rfq_id}`);
            if (rfqResponse && rfqResponse.success && rfqResponse.data) {
              rfq = rfqResponse.data;
            }
          } catch (rfqError) {
            console.warn('Error fetching RFQ details:', rfqError);
            // Continue without RFQ data if fetch fails
          }
        }
        
        setRfqDataState(rfq);
        
        // Extract buyer details from RFQ or company profile
        let buyerDetailsData = null;
        let buyerGstNumber = '';
        
        if (rfq) {
          // Get buyer details from RFQ first
          buyerDetailsData = {
            companyName: rfq.company_name || '',
            address: rfq.shipping_address || rfq.address || '',
            gstNumber: rfq.gst_number || '',
            state: rfq.shipping_state || rfq.state || '',
            country: rfq.shipping_country || rfq.country || 'India',
            contactPerson: rfq.buyer_name || rfq.name || '',
            email: rfq.buyer_email || rfq.email || '',
            phone: rfq.buyer_phone || rfq.phone || ''
          };
          buyerGstNumber = rfq.gst_number || '';
        }
        
        // Always fetch buyer GST from their company profile to ensure accuracy
        try {
          const buyerProfileResponse = await CommonService.get('/company/my-profile');
          if (buyerProfileResponse && buyerProfileResponse.success && buyerProfileResponse.data) {
            const profile = buyerProfileResponse.data;
            const addressObj = typeof profile.address === 'string' ? JSON.parse(profile.address || '{}') : (profile.address || {});
            
            // Get GST number - prioritize profile.tax_number, fallback to RFQ gst_number
            const finalBuyerGst = (profile.tax_number && profile.tax_number.trim()) 
              ? profile.tax_number.trim() 
              : (buyerGstNumber && buyerGstNumber.trim() ? buyerGstNumber.trim() : '');
            
            // Update buyer details with profile data (profile takes precedence for GST)
            buyerDetailsData = {
              companyName: buyerDetailsData?.companyName || profile.company_name || '',
              address: buyerDetailsData?.address || addressObj.street_address || addressObj.address || '',
              gstNumber: finalBuyerGst,
              state: buyerDetailsData?.state || addressObj.state || '',
              country: buyerDetailsData?.country || addressObj.country || 'India',
              contactPerson: buyerDetailsData?.contactPerson || profile.designation || '',
              email: buyerDetailsData?.email || profile.email || '',
              phone: buyerDetailsData?.phone || profile.phone || ''
            };
          }
        } catch (profileError) {
          console.warn('Error fetching buyer profile:', profileError);
        }
        
        setBuyerDetailsState(buyerDetailsData);
        
        // Extract seller details from quotation
        const sellerAddressObj = typeof quotation.seller_address === 'string' 
          ? JSON.parse(quotation.seller_address || '{}') 
          : (quotation.seller_address || {});
        const sellerContactObj = typeof quotation.seller_contact_details === 'string'
          ? JSON.parse(quotation.seller_contact_details || '{}')
          : (quotation.seller_contact_details || {});
        
        // Get seller GST number from quotation (now included in API response from company_profile.tax_number)
        // The backend query has been updated to include cp.tax_number as seller_gst_number
        let sellerGstNumber = '';
        
        // First, try to get from quotation response (from company_profile.tax_number)
        if (quotation.seller_gst_number && quotation.seller_gst_number.trim()) {
          sellerGstNumber = quotation.seller_gst_number.trim();
        }
        
        // If not in quotation response, try to fetch from seller's company profile
        if (!sellerGstNumber && quotation.seller_company_id) {
          try {
            const sellerProfileResponse = await CommonService.get(`/company/profile/${quotation.seller_company_id}`);
            if (sellerProfileResponse && sellerProfileResponse.success && sellerProfileResponse.data) {
              const taxNumber = sellerProfileResponse.data.tax_number;
              if (taxNumber && taxNumber.trim()) {
                sellerGstNumber = taxNumber.trim();
              }
            }
          } catch (companyIdError) {
            console.warn('Error fetching seller profile by company_id:', companyIdError);
          }
        }
        
        // Final fallback: try JSON fields
        if (!sellerGstNumber) {
          if (sellerAddressObj.gst_number && sellerAddressObj.gst_number.trim()) {
            sellerGstNumber = sellerAddressObj.gst_number.trim();
          } else if (sellerAddressObj.tax_number && sellerAddressObj.tax_number.trim()) {
            sellerGstNumber = sellerAddressObj.tax_number.trim();
          } else if (sellerContactObj.gst_number && sellerContactObj.gst_number.trim()) {
            sellerGstNumber = sellerContactObj.gst_number.trim();
          } else if (sellerContactObj.tax_number && sellerContactObj.tax_number.trim()) {
            sellerGstNumber = sellerContactObj.tax_number.trim();
          }
        }
        
        const sellerDetailsData = {
          companyName: quotation.seller_company_name || '',
          address: sellerAddressObj.street_address || sellerAddressObj.address || sellerAddressObj.full_address || '',
          gstNumber: sellerGstNumber,
          state: sellerAddressObj.state || '',
          country: sellerAddressObj.country || 'India',
          contactPerson: quotation.seller_name || sellerContactObj.name || '',
          email: quotation.seller_email || sellerContactObj.email || '',
          phone: quotation.seller_phone || sellerContactObj.phone || ''
        };
        
        setSellerDetailsState(sellerDetailsData);
        
        // Determine trade scenario based on locations
        const tradeScenarioData = determineTradeScenario(
          buyerDetailsData?.country || 'India',
          buyerDetailsData?.state || '',
          sellerDetailsData?.country || 'India',
          sellerDetailsData?.state || ''
        );
        
        // Update form data with fetched information
        setFormData(prev => {
          // Calculate Expected Delivery Date
          const poDate = prev.poDate || new Date().toISOString().split('T')[0];
          const poDateObj = new Date(poDate);
          let calculatedExpectedDeliveryDate = '';
          
          // Priority 1: Use quotation lead_time_days
          if (quotation.lead_time_days) {
            const deliveryDate = new Date(poDateObj);
            deliveryDate.setDate(deliveryDate.getDate() + parseInt(quotation.lead_time_days));
            calculatedExpectedDeliveryDate = deliveryDate.toISOString().split('T')[0];
          }
          // Priority 2: Use quotation delivery_time if it's a valid date
          else if (quotation.delivery_time) {
            try {
              const deliveryDate = new Date(quotation.delivery_time);
              if (!isNaN(deliveryDate.getTime())) {
                calculatedExpectedDeliveryDate = deliveryDate.toISOString().split('T')[0];
              }
            } catch (e) {
              // If delivery_time is not a valid date, ignore it
            }
          }
          // Priority 3: Use RFQ lead_time_days
          if (!calculatedExpectedDeliveryDate && rfq && rfq.lead_time_days) {
            const deliveryDate = new Date(poDateObj);
            deliveryDate.setDate(deliveryDate.getDate() + parseInt(rfq.lead_time_days));
            calculatedExpectedDeliveryDate = deliveryDate.toISOString().split('T')[0];
          }
          // Fallback: Default to 30 days from PO date if nothing is available
          if (!calculatedExpectedDeliveryDate) {
            const deliveryDate = new Date(poDateObj);
            deliveryDate.setDate(deliveryDate.getDate() + 30);
            calculatedExpectedDeliveryDate = deliveryDate.toISOString().split('T')[0];
          }
          
          return {
          ...prev,
          tradeScenario: tradeScenarioData.tradeScenario,
          tradeType: tradeScenarioData.tradeType,
          tradeLocation: tradeScenarioData.tradeLocation,
          buyerAddress: buyerDetailsData?.address || prev.buyerAddress,
          buyerGstNumber: (buyerDetailsData?.gstNumber && buyerDetailsData.gstNumber.trim()) 
            ? buyerDetailsData.gstNumber.trim() 
            : prev.buyerGstNumber,
          buyerState: buyerDetailsData?.state || prev.buyerState,
          buyerCountry: buyerDetailsData?.country || prev.buyerCountry,
          sellerAddress: sellerDetailsData?.address || prev.sellerAddress,
          sellerGstNumber: (sellerDetailsData?.gstNumber && sellerDetailsData.gstNumber.trim()) 
            ? sellerDetailsData.gstNumber.trim() 
            : prev.sellerGstNumber,
          sellerState: sellerDetailsData?.state || prev.sellerState,
          sellerCountry: sellerDetailsData?.country || prev.sellerCountry,
          // Map currency from quotation - match with dropdown options
          currency: (() => {
            if (!quotation.currency) return prev.currency;
            // Try to find matching currency in dropdown options
            if (currencyOptions && currencyOptions.length > 0) {
              const foundCurrency = currencyOptions.find(opt => 
                (opt.currencyCode && opt.currencyCode.toUpperCase() === String(quotation.currency).toUpperCase()) ||
                (opt.value && String(opt.value) === String(quotation.currency)) ||
                (opt.label && opt.label.toUpperCase().includes(String(quotation.currency).toUpperCase()))
              );
              if (foundCurrency) {
                return foundCurrency.currencyCode || foundCurrency.value || quotation.currency;
              }
            }
            return quotation.currency;
          })(),
          // Map payment terms from quotation - match with dropdown options
          paymentTerms: (() => {
            let paymentTermValue = null;
            if (quotation.payment_terms && Array.isArray(quotation.payment_terms) && quotation.payment_terms.length > 0) {
              paymentTermValue = quotation.payment_terms[0].label || quotation.payment_terms[0].value || quotation.payment_terms[0];
            } else if (typeof quotation.payment_terms === 'string') {
              paymentTermValue = quotation.payment_terms;
            }
            // Always set a value if available, even if master data isn't loaded yet
            if (paymentTermValue) {
              // Try to find matching payment term in dropdown options
              if (paymentScheduleOptions && paymentScheduleOptions.length > 0) {
                const foundTerm = paymentScheduleOptions.find(opt => 
                  opt.label === paymentTermValue ||
                  opt.value === paymentTermValue ||
                  (opt.label && opt.label.toLowerCase().includes(String(paymentTermValue).toLowerCase())) ||
                  (opt.value && String(opt.value).toLowerCase() === String(paymentTermValue).toLowerCase())
                );
                if (foundTerm) {
                  return foundTerm.label || paymentTermValue;
                }
              }
              // Return raw value if no match found (will be refined by second useEffect when master data loads)
              return paymentTermValue;
            }
            return prev.paymentTerms;
          })(),
          expectedDeliveryDate: calculatedExpectedDeliveryDate || prev.expectedDeliveryDate,
          // Map delivery terms and incoterms from quotation
          deliveryTerms: quotation.trade_terms || quotation.delivery_terms || prev.deliveryTerms,
          incoterms: quotation.incoterms || prev.incoterms,
          // Map freight terms from quotation (freight terms = incoterms) - always populate from incoterms
          freightTerms: (() => {
            // Always populate freight terms from incoterms value
            if (quotation.incoterms) {
              // Try to find matching freight term in dropdown options
              if (shippingTermsOptions && shippingTermsOptions.length > 0) {
                const foundTerm = shippingTermsOptions.find(opt => 
                  opt.label === quotation.incoterms ||
                  opt.value === quotation.incoterms ||
                  (opt.label && opt.label.toLowerCase().includes(String(quotation.incoterms).toLowerCase())) ||
                  (opt.value && String(opt.value).toLowerCase() === String(quotation.incoterms).toLowerCase())
                );
                if (foundTerm) {
                  return foundTerm.label || quotation.incoterms;
                }
              }
              // Return raw incoterms value if no match found (will be refined by second useEffect when master data loads)
              return quotation.incoterms;
            }
            return prev.freightTerms;
          })()
        };
        });
        
        // Populate line items from quotation items
        if (quotation.items && Array.isArray(quotation.items) && quotation.items.length > 0) {
          const mappedLineItems = quotation.items.map((item, index) => {
            // Use correct field names from database
            const quantity = parseFloat(item.quantity) || 0;
            const unitPrice = parseFloat(item.unit_price) || 0;
            const discount = parseFloat(item.discount) || 0; // Use item.discount (not discount_percentage)
            const subtotal = quantity * unitPrice;
            const discountAmount = subtotal * (discount / 100);
            const taxableAmount = subtotal - discountAmount;
            
            // Get tax rates from database - use actual values stored in line items
            // These values are already set based on the trade scenario when quotation was created
            // For intra-state trades, CGST might be stored in igst field (legacy compatibility)
            let cgst = parseFloat(item.cgst) || 0;
            const sgst = parseFloat(item.sgst) || 0; // Use item.sgst (not sgst_rate)
            const igst = parseFloat(item.igst) || 0; // Use item.igst (not igst_rate)
            const customs = parseFloat(item.customs) || 0; // Use item.customs (not customs_duty_rate)
            const cess = parseFloat(item.cess) || 0; // Use item.cess (not cess_rate)
            const tax = parseFloat(item.tax) || 0; // Use item.tax (not tax_rate)
            
            // For intra-state trades, if cgst is 0 but igst has a value, use igst as cgst
            // This handles legacy quotations where CGST was stored in igst field
            if (tradeScenarioData.tradeScenario === 'IN Intra-State (Same State in India)' && cgst === 0 && igst > 0) {
              cgst = igst;
            }
            
            // Map tax rates based on trade scenario (show only relevant taxes)
            let mappedCgst = 0;
            let mappedSgst = 0;
            let mappedIgst = 0;
            let mappedCustomsDuty = 0;
            let mappedCess = cess; // CESS is applicable in most scenarios
            let mappedTax = 0;
            
            if (tradeScenarioData.tradeScenario === 'IN Intra-State (Same State in India)') {
              // Intra-State: CGST + SGST + CESS
              mappedCgst = cgst;
              mappedSgst = sgst;
              mappedCess = cess;
            } else if (tradeScenarioData.tradeScenario === 'IN Inter-State (Between Indian States)') {
              // Inter-State: IGST + CESS
              mappedIgst = igst;
              mappedCess = cess;
            } else if (tradeScenarioData.tradeScenario === '→IN Import to India (Abroad → India)') {
              // Import: Customs Duty + IGST + CESS
              mappedCustomsDuty = customs;
              mappedIgst = igst;
              mappedCess = cess;
            } else if (tradeScenarioData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
              // Export: IGST + CESS (if Non-LUT), or 0% (if LUT)
              mappedIgst = igst;
              mappedCess = cess;
            } else if (tradeScenarioData.tradeScenario === 'Third Country (Abroad → Abroad)') {
              // Third Country: Tax
              mappedTax = tax;
            }
            
            // Calculate total based on trade scenario using actual tax amounts from database
            let total = 0;
            if (tradeScenarioData.tradeScenario === 'Third Country (Abroad → Abroad)') {
              const taxAmount = parseFloat(item.tax_amount) || (taxableAmount * (tax / 100));
              total = taxableAmount + taxAmount;
            } else if (tradeScenarioData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
              // For export: Use stored total or calculate based on IGST/CESS
              const igstAmount = parseFloat(item.igst_amount) || (taxableAmount * (igst / 100));
              const cessAmount = parseFloat(item.cess_amount) || (taxableAmount * (cess / 100));
              total = parseFloat(item.total) || (taxableAmount + igstAmount + cessAmount);
            } else if (isInternationalTrade(tradeScenarioData.tradeScenario)) {
              // For Import: Use assessable_value if available, otherwise calculate
              const customsAmount = parseFloat(item.customs_amount) || (taxableAmount * (customs / 100));
              const assessableValue = parseFloat(item.assessable_value) || (taxableAmount + customsAmount);
              const igstOnAssessable = parseFloat(item.igst_amount) || (assessableValue * (igst / 100));
              const cessOnAssessable = parseFloat(item.cess_amount) || (assessableValue * (cess / 100));
              total = parseFloat(item.total) || (assessableValue + igstOnAssessable + cessOnAssessable);
            } else if (tradeScenarioData.tradeScenario === 'IN Inter-State (Between Indian States)') {
              const igstAmount = parseFloat(item.igst_amount) || (taxableAmount * (igst / 100));
              const cessAmount = parseFloat(item.cess_amount) || (taxableAmount * (cess / 100));
              total = parseFloat(item.total) || (taxableAmount + igstAmount + cessAmount);
            } else {
              // Intra-State: CGST + SGST + CESS
              const cgstAmount = parseFloat(item.cgst_amount) || (taxableAmount * (cgst / 100));
              const sgstAmount = parseFloat(item.sgst_amount) || (taxableAmount * (sgst / 100));
              const cessAmount = parseFloat(item.cess_amount) || (taxableAmount * (cess / 100));
              total = parseFloat(item.total) || (taxableAmount + cgstAmount + sgstAmount + cessAmount);
            }
            
            return {
              id: index + 1,
              description: item.description || '', // Use item.description (not product_name)
              hsn: item.hsn_code || '', // Use item.hsn_code
              quantity: quantity,
              uom: item.uom || 'pcs', // Use item.uom (not unit or quantity_unit)
              unitPrice: unitPrice,
              discount: discount,
              cgst: mappedCgst,
              sgst: mappedSgst,
              igst: mappedIgst,
              customsDuty: mappedCustomsDuty,
              cess: mappedCess,
              tax: mappedTax,
              total: total
            };
          });
          
          setLineItems(mappedLineItems);
          
          // Populate delivery schedules from quotation if available
          // Delivery schedules might be stored as JSON in quotation or in a separate field
          let fetchedSchedules = [];
          
          if (quotation.delivery_schedule && Array.isArray(quotation.delivery_schedule) && quotation.delivery_schedule.length > 0) {
            // Parse delivery schedules from database
            fetchedSchedules = quotation.delivery_schedule.map((schedule, index) => {
              // Map database fields to UI format
              // Database might have: line_item_id, item_sequence, or reference to item_sequence
              return {
                id: schedule.id || schedule.schedule_id || (index + 1),
                itemSequence: schedule.item_sequence || schedule.line_item_id || schedule.item || null,
                quantity: parseFloat(schedule.quantity) || 0,
                deliveryDate: schedule.delivery_date || schedule.deliveryDate || '',
                location: schedule.location || schedule.delivery_location || ''
              };
            });
          } else if (quotation.delivery_schedules) {
            // Try alternative field name (JSON string or array)
            const schedules = typeof quotation.delivery_schedules === 'string' 
              ? JSON.parse(quotation.delivery_schedules || '[]')
              : (quotation.delivery_schedules || []);
            
            if (Array.isArray(schedules) && schedules.length > 0) {
              fetchedSchedules = schedules.map((schedule, index) => ({
                id: schedule.id || schedule.schedule_id || (index + 1),
                itemSequence: schedule.item_sequence || schedule.line_item_id || schedule.item || null,
                quantity: parseFloat(schedule.quantity) || 0,
                deliveryDate: schedule.delivery_date || schedule.deliveryDate || '',
                location: schedule.location || schedule.delivery_location || ''
              }));
            }
          }
          
          // Map delivery schedules to line item IDs after line items are loaded
          if (fetchedSchedules.length > 0 && mappedLineItems && mappedLineItems.length > 0) {
            // Use delivery schedules from database
            const mappedSchedules = fetchedSchedules.map(schedule => {
              // Find the line item by item_sequence (1-based) or by matching description
              let matchedItem = null;
              if (schedule.itemSequence) {
                // Try to match by item_sequence (1-based index)
                const itemIndex = parseInt(schedule.itemSequence) - 1;
                if (itemIndex >= 0 && itemIndex < mappedLineItems.length) {
                  matchedItem = mappedLineItems[itemIndex];
                }
              }
              
              // If no match found, try to match by first item or use first item as default
              if (!matchedItem && mappedLineItems.length > 0) {
                matchedItem = mappedLineItems[0];
              }
              
              return {
                id: schedule.id,
                item: matchedItem ? matchedItem.id : (mappedLineItems.length > 0 ? mappedLineItems[0].id : ''),
                quantity: schedule.quantity || (matchedItem ? matchedItem.quantity : 0),
                deliveryDate: schedule.deliveryDate,
                location: schedule.location
              };
            });
            setDeliverySchedules(mappedSchedules);
          } else if (mappedLineItems && mappedLineItems.length > 0) {
            // No delivery schedules in database, automatically create one for each line item
            const autoSchedules = mappedLineItems.map((lineItem, index) => ({
              id: index + 1,
              item: lineItem.id,
              quantity: lineItem.quantity,
              deliveryDate: '',
              location: ''
            }));
            setDeliverySchedules(autoSchedules);
          } else {
            // No line items, so no delivery schedules
            setDeliverySchedules([]);
          }
        } else {
          // No line items, so no delivery schedules
          setDeliverySchedules([]);
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load purchase order data');
        navigate('/purchase-order-list');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [location.state, navigate]);

  // Update payment terms, currency, and freight terms when master data is loaded
  useEffect(() => {
    if (!quotationDataState) return;

    setFormData(prev => {
      const updates = {};

      // Update currency if master data is available
      if (currencyOptions && currencyOptions.length > 0 && quotationDataState.currency) {
        const foundCurrency = currencyOptions.find(opt => 
          (opt.currencyCode && opt.currencyCode.toUpperCase() === String(quotationDataState.currency).toUpperCase()) ||
          (opt.value && String(opt.value) === String(quotationDataState.currency)) ||
          (opt.label && opt.label.toUpperCase().includes(String(quotationDataState.currency).toUpperCase()))
        );
        if (foundCurrency) {
          const newCurrency = foundCurrency.currencyCode || foundCurrency.value;
          if (prev.currency !== newCurrency) {
            updates.currency = newCurrency;
          }
        }
      }

      // Update payment terms if master data is available
      if (paymentScheduleOptions && paymentScheduleOptions.length > 0) {
        let paymentTermValue = null;
        if (quotationDataState.payment_terms && Array.isArray(quotationDataState.payment_terms) && quotationDataState.payment_terms.length > 0) {
          paymentTermValue = quotationDataState.payment_terms[0].label || quotationDataState.payment_terms[0].value;
        } else if (typeof quotationDataState.payment_terms === 'string') {
          paymentTermValue = quotationDataState.payment_terms;
        }
        if (paymentTermValue) {
          // Try exact match first
          let foundTerm = paymentScheduleOptions.find(opt => 
            opt.label === paymentTermValue || opt.value === paymentTermValue
          );
          // If no exact match, try partial match
          if (!foundTerm) {
            foundTerm = paymentScheduleOptions.find(opt => 
              (opt.label && opt.label.toLowerCase().includes(String(paymentTermValue).toLowerCase())) ||
              (opt.value && String(opt.value).toLowerCase().includes(String(paymentTermValue).toLowerCase()))
            );
          }
          if (foundTerm) {
            const newPaymentTerm = foundTerm.label || paymentTermValue;
            if (prev.paymentTerms !== newPaymentTerm) {
              updates.paymentTerms = newPaymentTerm;
            }
          } else if (!prev.paymentTerms && paymentTermValue) {
            // If no match found but we have a value and formData is empty, set it anyway
            updates.paymentTerms = paymentTermValue;
          }
        }
      }

      // Update freight terms from incoterms - always populate if incoterms is available
      if (quotationDataState.incoterms) {
        // If master data is available, try to match with dropdown options
        if (shippingTermsOptions && shippingTermsOptions.length > 0) {
          // Try exact match first
          let foundTerm = shippingTermsOptions.find(opt => 
            opt.label === quotationDataState.incoterms || opt.value === quotationDataState.incoterms
          );
          // If no exact match, try partial match
          if (!foundTerm) {
            foundTerm = shippingTermsOptions.find(opt => 
              (opt.label && opt.label.toLowerCase().includes(String(quotationDataState.incoterms).toLowerCase())) ||
              (opt.value && String(opt.value).toLowerCase().includes(String(quotationDataState.incoterms).toLowerCase()))
            );
          }
          if (foundTerm) {
            const newFreightTerm = foundTerm.label || quotationDataState.incoterms;
            if (prev.freightTerms !== newFreightTerm) {
              updates.freightTerms = newFreightTerm;
            }
          } else {
            // If no match found, still set the incoterms value
            if (prev.freightTerms !== quotationDataState.incoterms) {
              updates.freightTerms = quotationDataState.incoterms;
            }
          }
        } else {
          // If master data is not available yet, set the incoterms value directly
          if (prev.freightTerms !== quotationDataState.incoterms) {
            updates.freightTerms = quotationDataState.incoterms;
          }
        }
      }

      return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
    });
  }, [currencyOptions, paymentScheduleOptions, shippingTermsOptions, quotationDataState]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    if (name === 'tradeScenario') {
      if (value === 'IN Inter-State (Between Indian States)') {
        setFormData(prev => ({
          ...prev,
          tradeScenario: value,
          tradeType: 'Inter-State Trade (IGST)',
          tradeLocation: 'Karnataka ↔ Haryana'
        }));
      } else if (value === '→IN Import to India (Abroad → India)') {
        setFormData(prev => ({
          ...prev,
          tradeScenario: value,
          tradeType: 'Import: CIF + BCD + SWS + IGST',
          tradeLocation: 'United States → India'
        }));
      } else if (value === 'IN→ Export from India (India → Abroad)') {
        setFormData(prev => ({
          ...prev,
          tradeScenario: value,
          tradeType: 'Export: 0% IGST (with LUT)',
          tradeLocation: 'India → Germany'
        }));
      } else if (value === 'Third Country (Abroad → Abroad)') {
        setFormData(prev => ({
          ...prev,
          tradeScenario: value,
          tradeType: 'Third Country Trade',
          tradeLocation: 'United States → Singapore'
        }));
      } else {
        // IN Intra-State (Same State in India)
        setFormData(prev => ({
          ...prev,
          tradeScenario: value,
          tradeType: 'Intra-State Trade (CGST + SGST)',
          tradeLocation: 'Karnataka -> Karnataka'
        }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleLineItemChange = (id, field, value) => {
    // Clear validation error for this line item field when user starts typing
    const errorKey = `lineItem_${id}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
    
    setLineItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: field === 'description' ? value : (parseFloat(value) || 0) };
        // Recalculate total and tax amounts
        const subtotal = updated.quantity * updated.unitPrice;
        const discountAmount = subtotal * (updated.discount / 100);
        const taxableAmount = subtotal - discountAmount;
        const cgstAmount = taxableAmount * (updated.cgst / 100);
        const sgstAmount = taxableAmount * (updated.sgst / 100);
        const igstAmount = taxableAmount * (updated.igst / 100);
        const cessAmount = taxableAmount * (updated.cess / 100);
        
        // Calculate total based on trade scenario
        let total = 0;
        if (formData.tradeScenario === 'Third Country (Abroad → Abroad)') {
          const taxAmount = taxableAmount * ((updated.tax || 0) / 100);
          total = taxableAmount + taxAmount;
        } else if (formData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
          // For export: IGST and CESS are calculated on FOB Value (taxableAmount)
          if (formData.exportType === 'Non-LUT - IGST Applicable') {
            total = taxableAmount + igstAmount + cessAmount;
          } else {
            // LUT: No tax
            total = taxableAmount;
          }
        } else if (isInternationalTrade(formData.tradeScenario)) {
          // For International: CIF value + Customs Duty + IGST + CESS
          const customsDutyAmount = taxableAmount * (updated.customsDuty / 100);
          const assessableValue = taxableAmount + customsDutyAmount;
          const igstOnAssessable = assessableValue * (updated.igst / 100);
          const cessOnAssessable = assessableValue * (updated.cess / 100);
          total = assessableValue + igstOnAssessable + cessOnAssessable;
        } else if (formData.tradeScenario === 'IN Inter-State (Between Indian States)') {
          total = taxableAmount + igstAmount + cessAmount;
        } else {
          total = taxableAmount + cgstAmount + sgstAmount + cessAmount;
        }
        
        // Return updated item with all calculated amounts
        return {
          ...updated,
          subtotal: subtotal,
          discount_amount: discountAmount,
          discountAmount: discountAmount,
          fob_value: taxableAmount,
          fobValue: taxableAmount,
          cgst_amount: cgstAmount,
          cgstAmount: cgstAmount,
          sgst_amount: sgstAmount,
          sgstAmount: sgstAmount,
          igst_amount: igstAmount,
          igstAmount: igstAmount,
          cess_amount: cessAmount,
          cessAmount: cessAmount,
          total: total
        };
      }
      return item;
    }));
  };

  // Recalculate all line items when trade scenario changes
  useEffect(() => {
    setLineItems(prev => prev.map(item => {
      const subtotal = item.quantity * item.unitPrice;
      const discountAmount = subtotal * (item.discount / 100);
      const taxableAmount = subtotal - discountAmount;
      const cgstAmount = taxableAmount * (item.cgst / 100);
      const sgstAmount = taxableAmount * (item.sgst / 100);
      const igstAmount = taxableAmount * (item.igst / 100);
      const cessAmount = taxableAmount * (item.cess / 100);
      
      // Calculate total based on trade scenario
      let total = 0;
      if (formData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
        // For export: IGST and CESS are calculated on FOB Value (taxableAmount)
        if (formData.exportType === 'Non-LUT - IGST Applicable') {
          total = taxableAmount + igstAmount + cessAmount;
        } else {
          // LUT: No tax
          total = taxableAmount;
        }
      } else if (isInternationalTrade(formData.tradeScenario)) {
        const customsDutyAmount = taxableAmount * (item.customsDuty / 100);
        const assessableValue = taxableAmount + customsDutyAmount;
        const igstOnAssessable = assessableValue * (item.igst / 100);
        const cessOnAssessable = assessableValue * (item.cess / 100);
        total = assessableValue + igstOnAssessable + cessOnAssessable;
      } else if (formData.tradeScenario === 'IN Inter-State (Between Indian States)') {
        total = taxableAmount + igstAmount + cessAmount;
      } else {
        total = taxableAmount + cgstAmount + sgstAmount + cessAmount;
      }
      
      // Return item with all calculated amounts and totals
      return { 
        ...item, 
        subtotal: subtotal,
        discount_amount: discountAmount,
        discountAmount: discountAmount,
        fob_value: taxableAmount,
        fobValue: taxableAmount,
        cgst_amount: cgstAmount,
        cgstAmount: cgstAmount,
        sgst_amount: sgstAmount,
        sgstAmount: sgstAmount,
        igst_amount: igstAmount,
        igstAmount: igstAmount,
        cess_amount: cessAmount,
        cessAmount: cessAmount,
        total: total
      };
    }));
  }, [formData.tradeScenario, formData.exportType]);

  // Set IGST and CESS to 0 when LUT is selected for export
  useEffect(() => {
    if (formData.tradeScenario === 'IN→ Export from India (India → Abroad)' && 
        formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST') {
      setLineItems(prev => prev.map(item => ({
        ...item,
        igst: 0,
        cess: 0
      })));
    }
  }, [formData.exportType, formData.tradeScenario]);

  const handleDeleteLineItem = (id) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  };

  const handleAddLineItem = () => {
      const newId = Math.max(...lineItems.map(item => item.id), 0) + 1;
    setLineItems(prev => [...prev, {
      id: newId,
      description: '',
      hsn: '',
      quantity: 0,
      uom: 'pcs',
      unitPrice: 0,
      discount: 0,
      cgst: 9,
      sgst: 9,
      igst: 18,
      customsDuty: 10,
      cess: 0,
      total: 0
    }]);
  };

  const handleDeleteDeliverySchedule = (id) => {
    setDeliverySchedules(prev => prev.filter(item => item.id !== id));
  };

  const handleAddDeliverySchedule = () => {
    const newId = Math.max(...deliverySchedules.map(item => item.id), 0) + 1;
    const defaultItem = lineItems.length > 0 ? lineItems[0] : null;
    setDeliverySchedules(prev => [...prev, {
      id: newId,
      item: defaultItem ? defaultItem.id : '',
      quantity: defaultItem ? defaultItem.quantity : 0,
      deliveryDate: '',
      location: ''
    }]);
  };

  const handleDeliveryScheduleItemChange = (scheduleId, itemId) => {
    setDeliverySchedules(prev => prev.map(schedule => {
      if (schedule.id === scheduleId) {
        const selectedItem = lineItems.find(item => item.id.toString() === itemId.toString());
        return {
          ...schedule,
          item: itemId,
          quantity: selectedItem ? selectedItem.quantity : 0
        };
      }
      return schedule;
    }));
  };

  const handleDeleteTerm = (id) => {
    setTermsConditions(prev => prev.filter(item => item.id !== id));
  };

  const handleAddTerm = () => {
    const newId = Math.max(...termsConditions.map(item => item.id), 0) + 1;
    setTermsConditions(prev => [...prev, { id: newId, text: '' }]);
  };

  const handleTermChange = (id, value) => {
    setTermsConditions(prev => prev.map(term => 
      term.id === id ? { ...term, text: value } : term
    ));
  };

  const handleSpecialInstructionsChange = (e) => {
    setSpecialInstructions(e.target.value);
  };

  const handleDeleteAttachment = (id) => {
    setAttachments(prev => prev.filter(item => item.id !== id));
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file, index) => ({
      id: Math.max(...attachments.map(item => item.id), 0) + index + 1,
      name: file.name
    }));
    setAttachments(prev => [...prev, ...newAttachments]);
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => {
      const quantity = parseFloat(item.quantity) || 0;
      const unitPrice = parseFloat(item.unitPrice) || 0;
      return sum + (quantity * unitPrice);
    }, 0);

    const totalDiscount = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      return sum + (itemSubtotal * (item.discount / 100));
    }, 0);

    const taxableAmount = subtotal - totalDiscount;
    const cgst = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const discountAmount = itemSubtotal * (item.discount / 100);
      const itemTaxable = itemSubtotal - discountAmount;
      return sum + (itemTaxable * (item.cgst / 100));
    }, 0);

    const sgst = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const discountAmount = itemSubtotal * (item.discount / 100);
      const itemTaxable = itemSubtotal - discountAmount;
      return sum + (itemTaxable * (item.sgst / 100));
    }, 0);

    const igst = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const discountAmount = itemSubtotal * (item.discount / 100);
      const itemTaxable = itemSubtotal - discountAmount;
      return sum + (itemTaxable * (item.igst / 100));
    }, 0);

    const cess = lineItems.reduce((sum, item) => {
      const itemSubtotal = item.quantity * item.unitPrice;
      const discountAmount = itemSubtotal * (item.discount / 100);
      const itemTaxable = itemSubtotal - discountAmount;
      return sum + (itemTaxable * (item.cess / 100));
    }, 0);

    // Calculate FOB and CIF for import scenarios
    const fobValue = taxableAmount; // FOB = Subtotal - Discount
    const cifValue = isInternationalTrade(formData.tradeScenario)
      ? fobValue + internationalCharges.shippingCharges + internationalCharges.insuranceCharges + internationalCharges.handlingCharges
      : 0;

    // International-specific calculations
    // BCD is calculated on CIF value, not FOB
    // For import: BCD = CIF × Customs Duty % (using average customs duty rate)
    const basicCustomsDuty = isInternationalTrade(formData.tradeScenario)
      ? (() => {
          // Calculate average customs duty rate from line items
          const totalFOB = lineItems.reduce((sum, item) => {
            const itemSubtotal = item.quantity * item.unitPrice;
            const discountAmount = itemSubtotal * (item.discount / 100);
            return sum + (itemSubtotal - discountAmount);
          }, 0);
          const weightedCustomsRate = lineItems.reduce((sum, item) => {
            const itemSubtotal = item.quantity * item.unitPrice;
            const discountAmount = itemSubtotal * (item.discount / 100);
            const itemFOB = itemSubtotal - discountAmount;
            return sum + ((itemFOB / totalFOB) * (item.customsDuty / 100));
          }, 0);
          return cifValue * weightedCustomsRate;
        })()
      : lineItems.reduce((sum, item) => {
          const itemSubtotal = item.quantity * item.unitPrice;
          const discountAmount = itemSubtotal * (item.discount / 100);
          const itemTaxable = itemSubtotal - discountAmount;
          return sum + (itemTaxable * (item.customsDuty / 100));
        }, 0);

    const socialWelfareSurcharge = basicCustomsDuty * 0.10; // 10% of BCD
    const assessableValue = isInternationalTrade(formData.tradeScenario)
      ? cifValue + basicCustomsDuty + socialWelfareSurcharge
      : taxableAmount + basicCustomsDuty + socialWelfareSurcharge;
    
    // IGST on Assessable Value for International
    const igstOnAssessable = isInternationalTrade(formData.tradeScenario)
      ? assessableValue * 0.18 // 18% IGST on assessable value
      : igst;

    // Calculate tax for Third Country scenario
    const tax = formData.tradeScenario === 'Third Country (Abroad → Abroad)'
      ? lineItems.reduce((sum, item) => {
          const itemSubtotal = item.quantity * item.unitPrice;
          const discountAmount = itemSubtotal * (item.discount / 100);
          const itemTaxable = itemSubtotal - discountAmount;
          return sum + (itemTaxable * ((item.tax || 0) / 100));
        }, 0)
      : 0;

    // Calculate grand total based on trade scenario
    let grandTotal;
    if (formData.tradeScenario === 'Third Country (Abroad → Abroad)') {
      grandTotal = taxableAmount + tax;
    } else if (formData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
      // For export: IGST and CESS are calculated on FOB Value
      if (formData.exportType === 'Non-LUT - IGST Applicable') {
        grandTotal = taxableAmount + igst + cess;
      } else {
        // LUT: No tax
        grandTotal = taxableAmount;
      }
    } else if (isInternationalTrade(formData.tradeScenario)) {
      grandTotal = assessableValue + igstOnAssessable;
    } else if (formData.tradeScenario === 'IN Inter-State (Between Indian States)') {
      grandTotal = taxableAmount + igst + cess;
    } else {
      grandTotal = taxableAmount + cgst + sgst + cess;
    }

    return {
      subtotal,
      totalDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      igstOnAssessable,
      cess,
      tax,
      basicCustomsDuty,
      socialWelfareSurcharge,
      assessableValue,
      grandTotal,
      fobValue,
      cifValue
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const totals = calculateTotals();

  // Calculate international charges based on CIF config and FOB value
  useEffect(() => {
    if (isInternationalTrade(formData.tradeScenario)) {
      const fobValue = totals.taxableAmount;
      const shippingCharges = fobValue * (cifConfig.freightPercent / 100);
      const insuranceCharges = fobValue * (cifConfig.insurancePercent / 100);
      
      setInternationalCharges({
        shippingCharges: shippingCharges,
        insuranceCharges: insuranceCharges,
        handlingCharges: cifConfig.handlingFixed
      });
    }
  }, [cifConfig, totals.taxableAmount, formData.tradeScenario]);

  const handleCifConfigChange = (field, value) => {
    setCifConfig(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Validation function
  const validateForm = () => {
    const errors = {};

    // Basic required fields
    if (!formData.poDate) {
      errors.poDate = 'PO Date is required';
    }

    if (!formData.expectedDeliveryDate) {
      errors.expectedDeliveryDate = 'Expected Delivery Date is required';
    } else if (formData.poDate && formData.expectedDeliveryDate < formData.poDate) {
      errors.expectedDeliveryDate = 'Expected Delivery Date must be on or after PO Date';
    }

    if (!formData.paymentTerms) {
      errors.paymentTerms = 'Payment Terms is required';
    }

    if (!formData.currency) {
      errors.currency = 'Currency is required';
    }

    // Line items validation
    if (lineItems.length === 0) {
      errors.lineItems = 'At least one line item is required';
    } else {
      lineItems.forEach((item, index) => {
        if (!item.description || item.description.trim() === '') {
          errors[`lineItem_${item.id}_description`] = `Line item ${index + 1}: Description is required`;
        }
        if (!item.quantity || item.quantity <= 0) {
          errors[`lineItem_${item.id}_quantity`] = `Line item ${index + 1}: Quantity must be greater than 0`;
        }
        if (!item.unitPrice || item.unitPrice <= 0) {
          errors[`lineItem_${item.id}_unitPrice`] = `Line item ${index + 1}: Unit Price must be greater than 0`;
        }
      });
    }

    // Export-specific validation
    if (formData.tradeScenario === 'IN→ Export from India (India → Abroad)') {
      if (!formData.exportType) {
        errors.exportType = 'Export Type is required';
      } else if (formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST') {
        if (!formData.lutArn || formData.lutArn.trim() === '') {
          errors.lutArn = 'ARN Number is required when LUT is selected';
        }
      }
    }

    // International trade validation (optional fields, but validate if filled)
    if (isInternationalTrade(formData.tradeScenario)) {
      // These are optional, but if one is filled, others might be expected
      // We'll keep them optional for now as per business requirements
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveDraft = async () => {
    try {
      // Check if editing an existing draft PO
      const poIdFromState = location.state?.poId || location.state?.purchaseOrderId;
      
      // Get quotationId from location state or existing PO
      let quotationId = location.state?.quotationId;
      if (!quotationId && quotationDataState) {
        quotationId = quotationDataState.quotation_id;
      }
      
      // Ensure quotationId is a number
      if (quotationId && typeof quotationId === 'string') {
        // Extract numeric ID from formatted string (e.g., "QUO-2024-123456" -> 123456)
        const match = quotationId.match(/(\d+)$/);
        if (match && match[1]) {
          quotationId = parseInt(match[1], 10);
        } else {
          const parsed = parseInt(quotationId, 10);
          if (!isNaN(parsed)) {
            quotationId = parsed;
          }
        }
      }
      
      if ((!quotationId || isNaN(Number(quotationId))) && !poIdFromState) {
        toast.error('Quotation ID not found. Please select a quotation first.');
        return;
      }
      
      // Ensure quotationId is a number for the API
      quotationId = Number(quotationId);

      // Validate required fields
      if (!validateForm()) {
        const firstError = Object.values(validationErrors)[0];
        toast.error(firstError || 'Please fill in all required fields');
        // Scroll to first error field
        const firstErrorKey = Object.keys(validationErrors)[0];
        if (firstErrorKey && firstErrorKey.startsWith('lineItem_')) {
          // Scroll to line items section
          document.querySelector('.po-line-items-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Scroll to the field
          const field = document.querySelector(`[name="${firstErrorKey}"]`);
          field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Prepare PO data structure
      const poData = {
        quotation_id: quotationId,
        po_number: formData.poNumber,
        po_date: formData.poDate,
        po_status: 'Draft',
        // Send fields at root level for direct database mapping
        expected_delivery_date: formData.expectedDeliveryDate,
        priority_level: formData.priorityLevel,
        payment_terms: formData.paymentTerms,
        currency: formData.currency,
        freight_terms: formData.freightTerms, // Send at root level
        delivery_terms: formData.deliveryTerms,
        trade_scenario: formData.tradeScenario,
        trade_type: formData.tradeType,
        trade_location: formData.tradeLocation,
        po_data: {
          expectedDeliveryDate: formData.expectedDeliveryDate,
          priorityLevel: formData.priorityLevel,
          paymentTerms: formData.paymentTerms,
          currency: formData.currency,
          freightTerms: formData.freightTerms,
          deliveryTerms: formData.deliveryTerms,
          tradeScenario: formData.tradeScenario,
          tradeType: formData.tradeType,
          tradeLocation: formData.tradeLocation,
          buyerAddress: formData.buyerAddress,
          buyerGstNumber: formData.buyerGstNumber,
          buyerCountry: formData.buyerCountry,
          buyerState: formData.buyerState,
          sellerAddress: formData.sellerAddress,
          sellerGstNumber: formData.sellerGstNumber,
          sellerCountry: formData.sellerCountry,
          sellerState: formData.sellerState,
          portOfLoading: formData.portOfLoading,
          portOfDischarge: formData.portOfDischarge,
          customsBroker: formData.customsBroker,
          hsCode: formData.hsCode,
          countryOfOrigin: formData.countryOfOrigin,
          incoterms: formData.incoterms,
          exportType: formData.exportType,
          lutArn: formData.lutArn,
          cifConfig: cifConfig,
          lineItems: lineItems,
          deliverySchedules: deliverySchedules,
          termsConditions: termsConditions,
          specialInstructions: specialInstructions,
          attachments: attachments
        }
      };

      let response;
      if (poIdFromState) {
        // Update existing draft PO
        response = await CommonService.put(`/purchase-order/${poIdFromState}`, poData);
      } else {
        // Create new draft PO
        response = await CommonService.post('/purchase-order', poData);
      }
      
      if (response && response.success) {
        toast.success(poIdFromState ? 'Purchase order draft updated successfully' : 'Purchase order saved as draft successfully');
        // Navigate to purchase order list
        navigate('/purchase-order-list');
      } else {
        toast.error(response?.message || 'Failed to save purchase order as draft');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to save purchase order as draft');
    }
  };

  const handlePreview = () => {
    setShowPreviewModal(true);
  };

  const handleSubmit = async () => {
    try {
      // Check if editing an existing draft PO
      const poIdFromState = location.state?.poId || location.state?.purchaseOrderId;
      
      // Get quotationId from location state or existing PO
      let quotationId = location.state?.quotationId;
      if (!quotationId && quotationDataState) {
        quotationId = quotationDataState.quotation_id;
      }
      
      // Ensure quotationId is a number
      if (quotationId && typeof quotationId === 'string') {
        // Extract numeric ID from formatted string (e.g., "QUO-2024-123456" -> 123456)
        const match = quotationId.match(/(\d+)$/);
        if (match && match[1]) {
          quotationId = parseInt(match[1], 10);
        } else {
          const parsed = parseInt(quotationId, 10);
          if (!isNaN(parsed)) {
            quotationId = parsed;
          }
        }
      }
      
      if ((!quotationId || isNaN(Number(quotationId))) && !poIdFromState) {
        toast.error('Quotation ID not found. Please select a quotation first.');
        return;
      }
      
      // Ensure quotationId is a number for the API
      quotationId = Number(quotationId);

      // Validate required fields
      if (!validateForm()) {
        const firstError = Object.values(validationErrors)[0];
        toast.error(firstError || 'Please fill in all required fields');
        // Scroll to first error field
        const firstErrorKey = Object.keys(validationErrors)[0];
        if (firstErrorKey && firstErrorKey.startsWith('lineItem_')) {
          // Scroll to line items section
          document.querySelector('.po-line-items-card')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // Scroll to the field
          const field = document.querySelector(`[name="${firstErrorKey}"]`);
          field?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      // Prepare PO data structure
      const poData = {
        quotation_id: quotationId,
        po_number: formData.poNumber,
        po_date: formData.poDate,
        po_status: 'Submitted',
        // Send fields at root level for direct database mapping
        expected_delivery_date: formData.expectedDeliveryDate,
        priority_level: formData.priorityLevel,
        payment_terms: formData.paymentTerms,
        currency: formData.currency,
        freight_terms: formData.freightTerms, // Send at root level
        delivery_terms: formData.deliveryTerms,
        trade_scenario: formData.tradeScenario,
        trade_type: formData.tradeType,
        trade_location: formData.tradeLocation,
        po_data: {
          expectedDeliveryDate: formData.expectedDeliveryDate,
          priorityLevel: formData.priorityLevel,
          paymentTerms: formData.paymentTerms,
          currency: formData.currency,
          freightTerms: formData.freightTerms,
          deliveryTerms: formData.deliveryTerms,
          tradeScenario: formData.tradeScenario,
          tradeType: formData.tradeType,
          tradeLocation: formData.tradeLocation,
          buyerAddress: formData.buyerAddress,
          buyerGstNumber: formData.buyerGstNumber,
          buyerCountry: formData.buyerCountry,
          buyerState: formData.buyerState,
          sellerAddress: formData.sellerAddress,
          sellerGstNumber: formData.sellerGstNumber,
          sellerCountry: formData.sellerCountry,
          sellerState: formData.sellerState,
          portOfLoading: formData.portOfLoading,
          portOfDischarge: formData.portOfDischarge,
          customsBroker: formData.customsBroker,
          hsCode: formData.hsCode,
          countryOfOrigin: formData.countryOfOrigin,
          incoterms: formData.incoterms,
          exportType: formData.exportType,
          lutArn: formData.lutArn,
          cifConfig: cifConfig,
          lineItems: lineItems,
          deliverySchedules: deliverySchedules,
          termsConditions: termsConditions,
          specialInstructions: specialInstructions,
          attachments: attachments
        },
        // Also send at root level for direct database mapping
        seller_address: formData.sellerAddress,
        seller_gst_number: formData.sellerGstNumber,
        seller_country: formData.sellerCountry,
        seller_state: formData.sellerState,
        port_of_loading: formData.portOfLoading,
        port_of_discharge: formData.portOfDischarge,
        customs_broker: formData.customsBroker,
        hs_code: formData.hsCode,
        country_of_origin: formData.countryOfOrigin,
        incoterms: formData.incoterms,
        export_type: formData.exportType,
        lut_arn: formData.lutArn
      };

      let response;
      if (poIdFromState) {
        // Update existing draft PO and submit it
        response = await CommonService.put(`/purchase-order/${poIdFromState}`, poData);
      } else {
        // Create new PO and submit it
        response = await CommonService.post('/purchase-order', poData);
      }
      
      if (response && response.success) {
        const requiresApproval = response.data?.requires_buyer_approval === true;
        const msg =
          response.message ||
          (requiresApproval
            ? 'Purchase order submitted for sales order approval. Approvers have been notified.'
            : poIdFromState
              ? 'Purchase order updated and submitted successfully'
              : 'Purchase order submitted successfully');
        toast.success(msg);
        navigate('/purchase-order-list');
      } else {
        toast.error(response?.message || 'Failed to submit purchase order');
      }
    } catch (error) {
      console.error('Error submitting purchase order:', error);
      toast.error(error?.response?.data?.error || error?.message || 'Failed to submit purchase order');
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="po-creation">
        <div className="po-main-content">
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <p>Loading purchase order data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="po-creation">
      <div className="po-main-content">
        {/* Header */}
        <div className="po-header">
          {/* Breadcrumb and Buttons Row */}
          <div className="po-header-top-row">
            <nav aria-label="breadcrumb" className="po-breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a 
                    href="#" 
                    className="breadcrumb-link" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/purchase-order-list');
                    }}
                  >
                    Order Details
                  </a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Create Purchase Order
                </li>
              </ol>
            </nav>
            <div className="po-header-buttons">
              <button className="po-back-btn" onClick={() => navigate(-1)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                Back
              </button>
              <button className="po-btn-secondary" onClick={handleSaveDraft}>
                Save as Draft
              </button>
              <button className="po-btn-primary" onClick={handlePreview}>
                Preview & Submit Purchase Order
              </button>
            </div>
          </div>
          
          {/* Title and Trade Scenario Row */}
          <div className="po-header-content-row">
            <div className="po-header-left">
              <h1 className="po-main-title">Create Purchase Order</h1>
              <p className="po-subtitle">
                Create and finalize purchase order based on approved quotation {quotationData.quotationId}
              </p>
            </div>
            <div className="po-header-right">
              <div className="po-trade-scenario-wrapper">
                <div className="po-trade-scenario">
                  <label className="po-trade-scenario-label">Trade Scenario:</label>
                  <select 
                    className="po-trade-scenario-select"
                    value={formData.tradeScenario}
                    onChange={handleInputChange}
                    name="tradeScenario"
                    readOnly
                    disabled
                    style={{ cursor: 'not-allowed', opacity: 0.7 }}
                  >
                    <option>IN Intra-State (Same State in India)</option>
                    <option>IN Inter-State (Between Indian States)</option>
                    <option>→IN Import to India (Abroad → India)</option>
                    <option>IN→ Export from India (India → Abroad)</option>
                    <option>Third Country (Abroad → Abroad)</option>
                  </select>
                </div>
                <div className="po-trade-type-badge">
                  <span className="po-badge">{formData.tradeType}</span>
                </div>
                <div className="po-trade-location">
                  {formData.tradeLocation}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reference Quotation Card */}
        <div className="po-reference-quotation-card">
          <div className="po-reference-quotation-header">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="po-reference-icon">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <h3 className="po-section-title">Reference Quotation</h3>
          </div>
          <div className="po-reference-quotation-content">
            {/* First Row - 4 Columns */}
            <div className="po-reference-quotation-row po-reference-quotation-row-first">
              <div className="po-reference-quotation-item">
                <label>Quotation ID</label>
                <p className="po-reference-value">{quotationData.quotationId}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Linked RFQ</label>
                <p className="po-reference-value">{quotationData.linkedRFQ}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Quote Date</label>
                <p className="po-reference-value">{quotationData.quoteDate}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Valid Until</label>
                <p className="po-reference-value">{quotationData.validUntil}</p>
              </div>
            </div>
            
            {/* Horizontal Line */}
            <div className="po-reference-quotation-divider"></div>
            
            {/* Second Row - 4 Columns */}
            <div className="po-reference-quotation-row po-reference-quotation-row-second">
              <div className="po-reference-quotation-item">
                <label>Seller Company</label>
                <p className="po-reference-value">{quotationData.sellerCompany}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Person</label>
                <p className="po-reference-value">{quotationData.contactPerson}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Email</label>
                <p className="po-reference-value">{quotationData.contactEmail}</p>
              </div>
              <div className="po-reference-quotation-item">
                <label>Contact Phone</label>
                <p className="po-reference-value">{quotationData.contactPhone}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Purchase Order Details Card */}
        <div className="po-purchase-order-details-section">
          <h3 className="po-section-title">Purchase Order Details <span className="po-section-subtitle-inline">(Fill in the PO header information)</span></h3>
          <div className="po-purchase-order-details-content">
            <div className="po-form-grid">
              <div className="po-form-field">
                <label>PO Number</label>
                <input 
                  type="text" 
                  className="input-control" 
                  value={formData.poNumber}
                  readOnly
                />
              </div>
              <div className="po-form-field">
                <label>PO Date <span className="po-required">*</span></label>
                <input 
                  type="date" 
                  className={`input-control input-control-date ${validationErrors.poDate ? 'po-input-error' : ''}`}
                  name="poDate"
                  value={formData.poDate}
                  onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
                {validationErrors.poDate && (
                  <span className="po-error-message">{validationErrors.poDate}</span>
                )}
              </div>
              <div className="po-form-field">
                <label>Expected Delivery Date <span className="po-required">*</span></label>
                <input 
                  type="date" 
                  className={`input-control input-control-date ${validationErrors.expectedDeliveryDate ? 'po-input-error' : ''}`}
                  name="expectedDeliveryDate"
                  value={formData.expectedDeliveryDate}
                  onChange={handleInputChange}
                  min={formData.poDate || new Date().toISOString().split('T')[0]}
                  required
                />
                {validationErrors.expectedDeliveryDate && (
                  <span className="po-error-message">{validationErrors.expectedDeliveryDate}</span>
                )}
              </div>
              <div className="po-form-field">
                <label>Priority Level</label>
                <select 
                  className="input-control input-control-select"
                  name="priorityLevel"
                  value={formData.priorityLevel}
                  onChange={handleInputChange}
                >
                  <option>Low</option>
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </div>
            </div>
            
            {/* Divider */}
            <div className="po-reference-quotation-divider"></div>
            
            <div className="po-form-grid">
              <div className="po-form-field">
                <label>Payment Terms <span className="po-required">*</span></label>
                <select 
                  className={`input-control input-control-select ${validationErrors.paymentTerms ? 'po-input-error' : ''}`}
                  name="paymentTerms"
                  value={formData.paymentTerms}
                  onChange={handleInputChange}
                  required
                  disabled={incotermLoading}
                >
                  <option value="">{incotermLoading ? 'Loading payment terms...' : 'Select Payment Terms'}</option>
                  {paymentScheduleOptions && paymentScheduleOptions.length > 0 && (
                    paymentScheduleOptions.map((opt, idx) => (
                      <option key={`payment-term-${opt.value}-${idx}`} value={opt.label}>
                        {opt.label}
                      </option>
                    ))
                  )}
                  {/* Fallback options if master data is not available */}
                  {(!paymentScheduleOptions || paymentScheduleOptions.length === 0) && (
                    <>
                      <option>Net 15</option>
                      <option>Net 30</option>
                      <option>30 days from delivery</option>
                      <option>45 days from delivery</option>
                      <option>60 days from delivery</option>
                    </>
                  )}
                </select>
                {validationErrors.paymentTerms && (
                  <span className="po-error-message">{validationErrors.paymentTerms}</span>
                )}
              </div>
              <div className="po-form-field">
                <label>Currency <span className="po-required">*</span></label>
                <select 
                  className={`input-control input-control-select ${validationErrors.currency ? 'po-input-error' : ''}`}
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  required
                  disabled={currencyLoading}
                >
                  <option value="">{currencyLoading ? 'Loading currency...' : 'Select Currency'}</option>
                  {currencyOptions && currencyOptions.length > 0 && (
                    currencyOptions.map((opt, idx) => (
                      <option key={`currency-${opt.value}-${idx}`} value={opt.currencyCode || opt.value}>
                        {opt.label}
                      </option>
                    ))
                  )}
                  {/* Fallback options if master data is not available */}
                  {(!currencyOptions || currencyOptions.length === 0) && (
                    <>
                      <option value="INR">INR - Indian Rupee</option>
                      <option value="USD">USD - US Dollar</option>
                      <option value="EUR">EUR - Euro</option>
                      <option value="GBP">GBP - British Pound</option>
                      <option value="JPY">JPY - Japanese Yen</option>
                      <option value="CNY">CNY - Chinese Yuan</option>
                      <option value="AUD">AUD - Australian Dollar</option>
                      <option value="CAD">CAD - Canadian Dollar</option>
                      <option value="SGD">SGD - Singapore Dollar</option>
                      <option value="AED">AED - UAE Dirham</option>
                    </>
                  )}
                </select>
                {validationErrors.currency && (
                  <span className="po-error-message">{validationErrors.currency}</span>
                )}
              </div>
              <div className="po-form-field">
                <label>Freight Terms</label>
                <select 
                  className="input-control input-control-select"
                  name="freightTerms"
                  value={formData.freightTerms}
                  onChange={handleInputChange}
                  disabled={incotermLoading}
                >
                  <option value="">{incotermLoading ? 'Loading freight terms...' : 'Select Freight Terms'}</option>
                  {shippingTermsOptions && shippingTermsOptions.length > 0 && (
                    shippingTermsOptions.map((opt, idx) => (
                      <option key={`freight-term-${opt.value}-${idx}`} value={opt.label}>
                        {opt.label}
                      </option>
                    ))
                  )}
                  {/* Fallback options if master data is not available */}
                  {(!shippingTermsOptions || shippingTermsOptions.length === 0) && (
                    <>
                      <option>FOB (Free on Board)</option>
                      <option>CIF (Cost, Insurance and Freight)</option>
                      <option>EXW (Ex Works)</option>
                      <option>DDP (Delivered Duty Paid)</option>
                    </>
                  )}
                </select>
              </div>
              <div className="po-form-field">
                <label>Delivery Terms</label>
                <select 
                  className="input-control input-control-select"
                  name="deliveryTerms"
                  value={formData.deliveryTerms}
                  onChange={handleInputChange}
                >
                  <option value="">Select Delivery Terms</option>
                  <option value="Ex-Works">Ex-Works</option>
                  <option value="Door Delivery">Door Delivery</option>
                  <option value="Port Delivery">Port Delivery</option>
                  <option value="Warehouse Pickup">Warehouse Pickup</option>
                </select>
                {validationErrors.deliveryTerms && (
                  <span className="po-error-message">{validationErrors.deliveryTerms}</span>
                )}
              </div>
            </div>
            
            {/* International Trade Details - Conditional */}
            {isInternationalTrade(formData.tradeScenario) && (
              <div className="po-international-trade-section">
                <div className="po-international-trade-header">
                  <h3 className="po-international-trade-title">
                    <span className="po-international-trade-title-text">International Trade Details</span>
                  </h3>
                  <span className={`po-trade-badge ${
                    formData.tradeScenario === 'IN→ Export from India (India → Abroad)' 
                      ? 'po-export-badge' 
                      : formData.tradeScenario === 'Third Country (Abroad → Abroad)'
                      ? 'po-third-country-badge'
                      : 'po-import-badge'
                  }`}>
                    {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' 
                      ? 'Export' 
                      : formData.tradeScenario === 'Third Country (Abroad → Abroad)'
                      ? 'Third Country'
                      : 'Import'}
                  </span>
                </div>
                <div className="po-international-trade-grid">
                  <div className="po-form-field">
                    <label htmlFor="portOfLoading" className="po-form-label">Port of Loading</label>
                    <input
                      type="text"
                      id="portOfLoading"
                      className="input-control"
                      name="portOfLoading"
                      value={formData.portOfLoading || ''}
                      onChange={handleInputChange}
                      placeholder="Origin port"
                    />
                  </div>
                  <div className="po-form-field">
                    <label htmlFor="portOfDischarge" className="po-form-label">Port of Discharge</label>
                    <input
                      type="text"
                      id="portOfDischarge"
                      className="input-control"
                      name="portOfDischarge"
                      value={formData.portOfDischarge || ''}
                      onChange={handleInputChange}
                      placeholder="Destination port"
                    />
                  </div>
                  <div className="po-form-field">
                    <label htmlFor="customsBroker" className="po-form-label">Customs Broker/CHA</label>
                    <input
                      type="text"
                      id="customsBroker"
                      className="input-control"
                      name="customsBroker"
                      value={formData.customsBroker || ''}
                      onChange={handleInputChange}
                      placeholder="Customs handling agent"
                    />
                  </div>
                  <div className="po-form-field">
                    <label htmlFor="hsCode" className="po-form-label">HS Code (Harmonized System)</label>
                    <input
                      type="text"
                      id="hsCode"
                      className="input-control"
                      name="hsCode"
                      value={formData.hsCode || ''}
                      onChange={handleInputChange}
                      placeholder="8-digit HS code"
                    />
                  </div>
                  <div className="po-form-field">
                    <label htmlFor="countryOfOrigin" className="po-form-label">Country of Origin</label>
                    <input
                      type="text"
                      id="countryOfOrigin"
                      className="input-control"
                      name="countryOfOrigin"
                      value={formData.countryOfOrigin || ''}
                      onChange={handleInputChange}
                      placeholder="Manufacturing country"
                    />
                  </div>
                  <div className="po-form-field">
                    <label htmlFor="incoterms" className="po-form-label">Incoterms</label>
                    <select
                      id="incoterms"
                      className="input-control input-control-select"
                      name="incoterms"
                      value={formData.incoterms || ''}
                      onChange={handleInputChange}
                      disabled={incotermLoading}
                    >
                      <option value="">{incotermLoading ? 'Loading incoterms...' : 'Select Incoterms'}</option>
                      {shippingTermsOptions && shippingTermsOptions.length > 0 && (
                        shippingTermsOptions.map((opt, idx) => (
                          <option key={`incoterm-${opt.value}-${idx}`} value={opt.label}>
                            {opt.label}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
                
                {/* Export Tax Treatment - Only for Export scenarios */}
                {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' && (
                  <div className="po-export-tax-treatment">
                    <h4 className="po-export-tax-title">Export Tax Treatment</h4>
                    <div className="po-export-tax-grid">
                      <div className="po-form-field">
                        <label htmlFor="exportType" className="po-form-label">Export Type *</label>
                        <select
                          id="exportType"
                          className="input-control input-control-select"
                          name="exportType"
                          value={formData.exportType || ''}
                          onChange={handleInputChange}
                        >
                          <option value="LUT (Letter of Undertaking) - 0% IGST">LUT (Letter of Undertaking) - 0% IGST</option>
                          <option value="Non-LUT - IGST Applicable">Non-LUT - IGST Applicable</option>
                        </select>
                      </div>
                      {formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST' && (
                        <div className="po-form-field po-form-field-wide">
                          <label htmlFor="lutArn" className="po-form-label">ARN Number (Application Reference Number) *</label>
                          <input
                            type="text"
                            id="lutArn"
                            className={`input-control ${validationErrors.lutArn ? 'po-input-error' : ''}`}
                            name="lutArn"
                            value={formData.lutArn || ''}
                            onChange={handleInputChange}
                            placeholder="AA341234567890Z"
                          />
                          {validationErrors.lutArn && (
                            <span className="po-error-message">{validationErrors.lutArn}</span>
                          )}
                          <p className="po-form-hint">ARN for LUT approved by GST department</p>
                        </div>
                      )}
                      {formData.exportType === 'Non-LUT - IGST Applicable' && (
                        <div className="po-form-field po-form-field-wide">
                          <div className="po-export-warning-box">
                            <p className="po-export-warning-text">⚠️ IGST will be charged on FOB value. Refund can be claimed after export with shipping bills.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="po-required-documents-box">
                  <p className="po-required-documents-text">
                    <strong>Required Documents:</strong> Commercial Invoice, Packing List, Bill of Lading/Airway Bill, Certificate of Origin, Insurance Certificate, Bill of Entry, Import License (if applicable)
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Buyer & Seller Details Cards */}
        <div className="po-details-grid">
          <div className="po-purchase-order-details-section">
            <div className="po-reference-quotation-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-reference-icon">
                <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                <path d="M10 6h4"></path>
                <path d="M10 10h4"></path>
                <path d="M10 14h4"></path>
                <path d="M10 18h4"></path>
              </svg>
              <h3 className="po-section-title">Buyer Details</h3>
            </div>
            <div className="po-purchase-order-details-content">
              <div className="po-details-list">
                <div className="po-detail-item">
                  <label>Company Name</label>
                  <div className="po-detail-value">{buyerDetails.companyName}</div>
                </div>
                <div className="po-detail-item">
                  <label>Address</label>
                  <input 
                    type="text" 
                    className="input-control" 
                    name="buyerAddress"
                    value={formData.buyerAddress}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="po-detail-item">
                  <label>GST Number</label>
                  <input 
                    type="text" 
                    className="input-control" 
                    name="buyerGstNumber"
                    value={formData.buyerGstNumber}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="po-detail-item po-detail-item-row">
                  <div className="po-detail-item-half">
                    <label>Country</label>
                    <select
                      className="input-control input-control-select"
                      name="buyerCountry"
                      value={formData.buyerCountry}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Country</option>
                      {countryOptions.map((country, index) => (
                        <option key={index} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="po-detail-item-half">
                    <label>State</label>
                    <select
                      className="input-control input-control-select"
                      name="buyerState"
                      value={formData.buyerState}
                      onChange={handleInputChange}
                    >
                      <option value="">Select State</option>
                      {stateOptions.map((state, index) => (
                        <option key={index} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="po-detail-item po-detail-item-row-3">
                  <div className="po-detail-item-third">
                    <label>Contact Person</label>
                    <div className="po-detail-value">{buyerDetails.contactPerson}</div>
                  </div>
                  <div className="po-detail-item-third">
                    <label>Email</label>
                    <div className="po-detail-value">{buyerDetails.email}</div>
                  </div>
                  <div className="po-detail-item-third">
                    <label>Phone</label>
                    <div className="po-detail-value">{buyerDetails.phone}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="po-reference-quotation-card">
            <div className="po-reference-quotation-header">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-reference-icon">
                <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
                <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
                <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
                <path d="M10 6h4"></path>
                <path d="M10 10h4"></path>
                <path d="M10 14h4"></path>
                <path d="M10 18h4"></path>
              </svg>
              <h3 className="po-section-title">Seller/Vendor Details</h3>
            </div>
            <div className="po-purchase-order-details-content">
              <div className="po-details-list">
                <div className="po-detail-item">
                  <label>Company Name</label>
                  <div className="po-detail-value">{sellerDetails.companyName}</div>
                </div>
                <div className="po-detail-item">
                  <label>Address</label>
                  <input 
                    type="text" 
                    className="input-control" 
                    name="sellerAddress"
                    value={formData.sellerAddress}
                    onChange={handleInputChange}
                    readOnly
                  />
                </div>
                <div className="po-detail-item">
                  <label>GST Number</label>
                  <input 
                    type="text" 
                    className="input-control" 
                    name="sellerGstNumber"
                    value={formData.sellerGstNumber}
                    onChange={handleInputChange}
                    readOnly
                  />
                </div>
                <div className="po-detail-item po-detail-item-row">
                  <div className="po-detail-item-half">
                    <label>Country</label>
                    <input
                      type="text"
                      className="input-control"
                      name="sellerCountry"
                      value={formData.sellerCountry}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                  <div className="po-detail-item-half">
                    <label>State</label>
                    <input
                      type="text"
                      className="input-control"
                      name="sellerState"
                      value={formData.sellerState}
                      onChange={handleInputChange}
                      readOnly
                    />
                  </div>
                </div>
                <div className="po-detail-item po-detail-item-row-3">
                  <div className="po-detail-item-third">
                    <label>Contact Person</label>
                    <div className="po-detail-value">{sellerDetails.contactPerson}</div>
                  </div>
                  <div className="po-detail-item-third">
                    <label>Email</label>
                    <div className="po-detail-value">{sellerDetails.email}</div>
                  </div>
                  <div className="po-detail-item-third">
                    <label>Phone</label>
                    <div className="po-detail-value">{sellerDetails.phone}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CIF Value Configuration - Only for Import scenarios */}
        {formData.tradeScenario === '→IN Import to India (Abroad → India)' && (
          <div className="po-cif-config-card">
            <div className="po-cif-config-header">
              <h4 className="po-cif-config-title">CIF Value Configuration</h4>
              <p className="po-cif-config-subtitle">
                Configure freight, insurance, and handling charges to calculate CIF (Cost, Insurance, Freight) value
              </p>
            </div>
            <div className="po-cif-config-content">
              <div className="po-cif-config-grid">
                <div className="po-cif-config-field">
                  <label htmlFor="freightPercent" className="po-cif-config-label">
                    Freight Charges (%)
                  </label>
                  <input
                    type="number"
                    id="freightPercent"
                    className="po-cif-config-input"
                    step="0.1"
                    placeholder="% of FOB value"
                    value={cifConfig.freightPercent}
                    onChange={(e) => handleCifConfigChange('freightPercent', e.target.value)}
                  />
                  <p className="po-cif-config-hint">Percentage of FOB value</p>
                </div>
                <div className="po-cif-config-field">
                  <label htmlFor="insurancePercent" className="po-cif-config-label">
                    Insurance Charges (%)
                  </label>
                  <input
                    type="number"
                    id="insurancePercent"
                    className="po-cif-config-input"
                    step="0.1"
                    placeholder="% of FOB value"
                    value={cifConfig.insurancePercent}
                    onChange={(e) => handleCifConfigChange('insurancePercent', e.target.value)}
                  />
                  <p className="po-cif-config-hint">Percentage of FOB value</p>
                </div>
                <div className="po-cif-config-field">
                  <label htmlFor="handlingFixed" className="po-cif-config-label">
                    Handling & Clearance (₹)
                  </label>
                  <input
                    type="number"
                    id="handlingFixed"
                    className="po-cif-config-input"
                    placeholder="Fixed amount"
                    value={cifConfig.handlingFixed}
                    onChange={(e) => handleCifConfigChange('handlingFixed', e.target.value)}
                  />
                  <p className="po-cif-config-hint">Fixed amount in INR</p>
                </div>
              </div>
              <div className="po-cif-calculation-info">
                <p className="po-cif-calculation-title"><strong>CIF Calculation:</strong> FOB Value + Freight ({cifConfig.freightPercent}%) + Insurance ({cifConfig.insurancePercent}%) + Handling (₹{formatCurrency(cifConfig.handlingFixed)})</p>
                <p className="po-cif-calculation-note">As per Indian Customs law, Basic Customs Duty (BCD) and subsequent taxes are calculated on the CIF value, not FOB.</p>
              </div>
            </div>
          </div>
        )}

        {/* Line Items Card - Visible for all trade scenarios */}
        {(formData.tradeScenario === 'IN Intra-State (Same State in India)' || formData.tradeScenario === 'IN Inter-State (Between Indian States)' || isInternationalTrade(formData.tradeScenario)) && (
          <div className="po-line-items-card">
            <div className="po-line-items-header">
              <div className="po-line-items-header-left">
                <h4 className="po-line-items-title">
                  Line Items <span className="po-required">*</span>
                  {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' && (
                    <span className="po-export-badge-inline">Export: IGST & CESS ✓</span>
                  )}
                </h4>
                <p className="po-line-items-subtitle">
                  Review and edit items from quotation. Add additional items like packaging, installation, or freight charges as needed
                </p>
                {validationErrors.lineItems && (
                  <span className="po-error-message">{validationErrors.lineItems}</span>
                )}
              </div>
              <button className="po-btn-add-line-item" onClick={handleAddLineItem} type="button">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-add-icon">
                  <path d="M5 12h14"></path>
                  <path d="M12 5v14"></path>
                </svg>
                Add Line Item
              </button>
            </div>
            <div className="po-line-items-content">
              <div className="po-table-wrapper">
                <table className="po-line-items-table">
                  <thead>
                    <tr>
                      <th className="po-table-col-number">#</th>
                      <th className="po-table-col-description">Description</th>
                      <th>HSN</th>
                      <th className="po-table-col-qty">Qty</th>
                      <th>UOM</th>
                      <th className="po-table-col-price">Unit Price (₹)</th>
                      <th className="po-table-col-disc">Disc %</th>
                      {isInternationalTrade(formData.tradeScenario) ? (
                        <>
                          {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' ? (
                            <>
                              <th className="po-table-col-tax po-table-col-igst-export">IGST %</th>
                              <th className="po-table-col-tax po-table-col-cess-export">CESS %</th>
                            </>
                          ) : formData.tradeScenario === 'Third Country (Abroad → Abroad)' ? (
                            <>
                              <th className="po-table-col-tax">Tax %</th>
                            </>
                          ) : (
                            <>
                              <th className="po-table-col-tax">Customs %</th>
                              <th className="po-table-col-tax">IGST %</th>
                              <th className="po-table-col-tax">CESS %</th>
                            </>
                          )}
                        </>
                      ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                        <>
                          <th className="po-table-col-tax">IGST %</th>
                          <th className="po-table-col-tax">CESS %</th>
                        </>
                      ) : (
                        <>
                          <th className="po-table-col-tax">CGST %</th>
                          <th className="po-table-col-tax">SGST %</th>
                          <th className="po-table-col-tax">CESS %</th>
                        </>
                      )}
                      <th className="po-table-col-total-header">Total (₹)</th>
                      <th className="po-table-col-action">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={item.id}>
                        <td>{index + 1}</td>
                        <td>
                          <input 
                            type="text" 
                            className={`input-control ${validationErrors[`lineItem_${item.id}_description`] ? 'po-input-error' : ''}`}
                            value={item.description}
                            onChange={(e) => handleLineItemChange(item.id, 'description', e.target.value)}
                            title={validationErrors[`lineItem_${item.id}_description`] || ''}
                          />
                          {validationErrors[`lineItem_${item.id}_description`] && (
                            <div className="po-line-item-error">{validationErrors[`lineItem_${item.id}_description`]}</div>
                          )}
                        </td>
                        <td>
                          <input 
                            type="text" 
                            className="input-control input-control-hsn"
                            value={item.hsn}
                            onChange={(e) => handleLineItemChange(item.id, 'hsn', e.target.value)}
                          />
                        </td>
                        <td>
                          <input 
                            type="number" 
                            className={`input-control input-control-number ${validationErrors[`lineItem_${item.id}_quantity`] ? 'po-input-error' : ''}`}
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(item.id, 'quantity', e.target.value)}
                            title={validationErrors[`lineItem_${item.id}_quantity`] || ''}
                          />
                          {validationErrors[`lineItem_${item.id}_quantity`] && (
                            <div className="po-line-item-error">{validationErrors[`lineItem_${item.id}_quantity`]}</div>
                          )}
                        </td>
                        <td>
                          <select 
                            className="input-control input-control-select"
                            value={item.uom}
                            onChange={(e) => handleLineItemChange(item.id, 'uom', e.target.value)}
                          >
                            <option value="pcs">pcs</option>
                            <option value="units">units</option>
                            <option value="kg">kg</option>
                            <option value="m">m</option>
                            <option value="sqm">sqm</option>
                          </select>
                        </td>
                        <td>
                          <input 
                            type="number" 
                            className={`input-control input-control-number ${validationErrors[`lineItem_${item.id}_unitPrice`] ? 'po-input-error' : ''}`}
                            value={item.unitPrice}
                            onChange={(e) => handleLineItemChange(item.id, 'unitPrice', e.target.value)}
                            title={validationErrors[`lineItem_${item.id}_unitPrice`] || ''}
                          />
                          {validationErrors[`lineItem_${item.id}_unitPrice`] && (
                            <div className="po-line-item-error">{validationErrors[`lineItem_${item.id}_unitPrice`]}</div>
                          )}
                        </td>
                        <td>
                          <input 
                            type="number" 
                            className="input-control input-control-number"
                            value={item.discount}
                            onChange={(e) => handleLineItemChange(item.id, 'discount', e.target.value)}
                          />
                        </td>
                        {isInternationalTrade(formData.tradeScenario) ? (
                          <>
                            {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' ? (
                              <>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST' ? 0 : item.igst}
                                    onChange={(e) => handleLineItemChange(item.id, 'igst', e.target.value)}
                                    readOnly={formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST'}
                                    placeholder="0 (LUT)"
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST' ? 0 : item.cess}
                                    onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                                    readOnly={formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST'}
                                    placeholder="0 (LUT)"
                                  />
                                </td>
                              </>
                            ) : formData.tradeScenario === 'Third Country (Abroad → Abroad)' ? (
                              <>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={item.tax || 0}
                                    onChange={(e) => handleLineItemChange(item.id, 'tax', e.target.value)}
                                    placeholder="VAT/GST"
                                  />
                                </td>
                              </>
                            ) : (
                              <>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={item.customsDuty || 0}
                                    onChange={(e) => handleLineItemChange(item.id, 'customsDuty', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={item.igst}
                                    onChange={(e) => handleLineItemChange(item.id, 'igst', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input 
                                    type="number" 
                                    className="input-control input-control-number input-control-tax"
                                    value={item.cess}
                                    onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                                  />
                                </td>
                              </>
                            )}
                          </>
                        ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                          <>
                            <td>
                              <input 
                                type="number" 
                                className="input-control input-control-number input-control-tax"
                                value={item.igst}
                                onChange={(e) => handleLineItemChange(item.id, 'igst', e.target.value)}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                className="input-control input-control-number input-control-tax"
                                value={item.cess}
                                onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                              />
                            </td>
                          </>
                        ) : (
                          <>
                            <td>
                              <input 
                                type="number" 
                                className="input-control input-control-number input-control-tax"
                                value={item.cgst}
                                onChange={(e) => handleLineItemChange(item.id, 'cgst', e.target.value)}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                className="input-control input-control-number input-control-tax"
                                value={item.sgst}
                                onChange={(e) => handleLineItemChange(item.id, 'sgst', e.target.value)}
                              />
                            </td>
                            <td>
                              <input 
                                type="number" 
                                className="input-control input-control-number input-control-tax"
                                value={item.cess}
                                onChange={(e) => handleLineItemChange(item.id, 'cess', e.target.value)}
                              />
                            </td>
                          </>
                        )}
                        <td className="po-table-col-total-cell">{formatCurrency(item.total)}</td>
                        <td>
                          <button 
                            className="po-btn-delete-item"
                            onClick={() => handleDeleteLineItem(item.id)}
                            type="button"
                            title="Remove item"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-delete-icon">
                              <path d="M10 11v6"></path>
                              <path d="M14 11v6"></path>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                              <path d="M3 6h18"></path>
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {/* Summary Rows */}
                    {formData.tradeScenario === 'IN→ Export from India (India → Abroad)' ? (
                      <>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Subtotal (Qty × Unit Price):</td>
                          <td colSpan="7"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.subtotal)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Total Discount:</td>
                          <td colSpan="7"></td>
                          <td className="po-table-summary-value" colSpan="1">- <span className="font-semibold">{formatCurrency(totals.totalDiscount)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row po-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">FOB Value (Free on Board):</span>
                          </td>
                          <td colSpan="7"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.subtotal - totals.totalDiscount)}</span>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                        {formData.exportType === 'LUT (Letter of Undertaking) - 0% IGST' && (
                          <tr className="po-table-summary-row po-breakdown-green">
                            <td colSpan="1"></td>
                            <td colSpan="1" className="po-table-summary-label text-green-700">
                              <em>✓ Export with LUT (Letter of Undertaking) - 0% IGST</em><br />
                              <span className="text-xs">ARN: {formData.lutArn || 'AA341234567890Z'}</span>
                            </td>
                            <td colSpan="7"></td>
                            <td className="po-table-summary-value text-right text-green-700" colSpan="1">
                              <em>No Tax</em>
                            </td>
                            <td colSpan="2"></td>
                          </tr>
                        )}
                        {formData.exportType === 'Non-LUT - IGST Applicable' && (
                          <>
                            <tr className="po-table-summary-row">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="po-table-summary-label">IGST:</td>
                              <td colSpan="7"></td>
                              <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.igst)}</span></td>
                              <td colSpan="2"></td>
                            </tr>
                            <tr className="po-table-summary-row">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="po-table-summary-label">CESS:</td>
                              <td colSpan="7"></td>
                              <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                              <td colSpan="2"></td>
                            </tr>
                            <tr className="po-table-summary-row po-breakdown-amber">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="po-table-summary-label">
                                <span className="po-export-warning-flex">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-export-warning-icon">
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                    <path d="M12 9v4"></path>
                                    <path d="M12 17h.01"></path>
                                  </svg>
                                  Export without LUT - IGST Amount (Refundable):
                                </span>
                              </td>
                              <td colSpan="7"></td>
                              <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.igst)}</span></td>
                              <td colSpan="2"></td>
                            </tr>
                            <tr className="po-table-summary-row po-breakdown-amber">
                              <td colSpan="1"></td>
                              <td colSpan="1" className="po-table-summary-label">
                                <span className="po-export-warning-flex">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-export-warning-icon">
                                    <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                                    <path d="M12 9v4"></path>
                                    <path d="M12 17h.01"></path>
                                  </svg>
                                  Export without LUT - CESS Amount (Refundable):
                                </span>
                              </td>
                              <td colSpan="7"></td>
                              <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                              <td colSpan="2"></td>
                            </tr>
                          </>
                        )}
                      </>
                    ) : formData.tradeScenario === 'Third Country (Abroad → Abroad)' ? (
                      <>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Subtotal (Qty × Unit Price):</td>
                          <td colSpan="6"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.subtotal)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Total Discount:</td>
                          <td colSpan="6"></td>
                          <td className="po-table-summary-value" colSpan="1">- <span className="font-semibold">{formatCurrency(totals.totalDiscount)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row po-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">FOB Value (Free on Board):</span>
                          </td>
                          <td colSpan="6"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.subtotal - totals.totalDiscount)}</span>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                      </>
                    ) : formData.tradeScenario !== '→IN Import to India (Abroad → India)' ? (
                      <>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Subtotal (Qty × Unit Price):</td>
                          <td colSpan={formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? "7" : "8"}></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.subtotal)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Total Discount:</td>
                          <td colSpan={formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? "7" : "8"}></td>
                          <td className="po-table-summary-value" colSpan="1">- <span className="font-semibold">{formatCurrency(totals.totalDiscount)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label"><span className="font-semibold">Taxable Amount:</span></td>
                          <td colSpan={formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? "7" : "8"}></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.taxableAmount)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                      </>
                    ) : null}
                          {formData.tradeScenario === '→IN Import to India (Abroad → India)' ? (
                      <>
                        <tr className="po-table-summary-row po-table-landed-cost">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">Sum of All Line Items (Landed Cost):</span>
                          </td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.grandTotal)}</span>
                          </td>
                          <td colSpan="1"></td>
                        </tr>
                        {/* Detailed Breakdown in Table */}
                        <tr className="po-table-breakdown-divider">
                          <td colSpan="13" className="po-table-summary-label text-center text-xs italic py-2">
                            ──── Detailed Breakdown Below (For Reference Only) ────
                          </td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-gray">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Subtotal (Qty × Unit Price):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.subtotal)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-gray">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Total Discount:</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1">- <span className="font-semibold">{formatCurrency(totals.totalDiscount)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-blue">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">FOB Value (Free on Board):</span>
                          </td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.fobValue)}</span>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-amber">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">+ Freight Charges ({cifConfig.freightPercent}% of FOB):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(internationalCharges.shippingCharges)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-amber">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">+ Insurance Charges ({cifConfig.insurancePercent}% of FOB):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(internationalCharges.insuranceCharges)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-amber">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">+ Handling & Clearance (Fixed):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(internationalCharges.handlingCharges)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-blue-bold">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">= CIF Value (Cost + Insurance + Freight):</span>
                          </td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.cifValue)}</span>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-purple">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Basic Customs Duty (BCD on CIF):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.basicCustomsDuty)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-purple">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">Social Welfare Surcharge (10% of BCD):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.socialWelfareSurcharge)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-blue-bold">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">
                            <span className="font-semibold">= Assessable Value (CIF + BCD + SWS):</span>
                          </td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1">
                            <span className="font-semibold">{formatCurrency(totals.assessableValue)}</span>
                          </td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-breakdown-row po-breakdown-green">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">IGST (on Assessable Value):</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.igstOnAssessable)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                      </>
                    ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                      <>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">IGST:</td>
                          <td colSpan="7"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.igst)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">CESS:</td>
                          <td colSpan="7"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                      </>
                    ) : formData.tradeScenario === 'IN Intra-State (Same State in India)' ? (
                      <>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">CGST:</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.cgst)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">SGST:</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.sgst)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                        <tr className="po-table-summary-row">
                          <td colSpan="1"></td>
                          <td colSpan="1" className="po-table-summary-label">CESS:</td>
                          <td colSpan="8"></td>
                          <td className="po-table-summary-value" colSpan="1"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                          <td colSpan="2"></td>
                        </tr>
                      </>
                    ) : null}
                    <tr className={`po-table-summary-row po-table-grand-total ${isInternationalTrade(formData.tradeScenario) && formData.tradeScenario !== 'IN→ Export from India (India → Abroad)' ? 'po-breakdown-green-bold' : formData.tradeScenario === 'IN→ Export from India (India → Abroad)' || formData.tradeScenario === 'Third Country (Abroad → Abroad)' ? 'po-breakdown-green-bold' : ''}`}>
                      <td colSpan="1"></td>
                      <td colSpan="1" className="po-table-summary-label">
                        <span className="font-semibold">Grand Total (Sum of Line Items):</span>
                      </td>
                      <td colSpan={formData.tradeScenario === 'Third Country (Abroad → Abroad)' ? "6" : formData.tradeScenario === 'IN Inter-State (Between Indian States)' || formData.tradeScenario === 'IN→ Export from India (India → Abroad)' ? "7" : "8"}></td>
                      <td className="po-table-summary-value" colSpan="1">
                        <span className="font-semibold">{formatCurrency(totals.grandTotal)}</span>
                      </td>
                      <td colSpan="2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Import Calculation Verification */}
            {formData.tradeScenario === '→IN Import to India (Abroad → India)' && (
              <div className="po-import-verification">
                <p className="po-verification-title"><strong>Import Calculation Verification:</strong></p>
                <div className="po-verification-content">
                  <p>• Subtotal (Σ Qty × Unit Price) = {formatCurrency(totals.subtotal)}</p>
                  <p>• Less: Total Discount = {formatCurrency(totals.totalDiscount)}</p>
                  <p>• FOB Value (Subtotal - Discount) = {formatCurrency(totals.fobValue)}</p>
                  <p className="po-verification-checkmark">✓ Each line item total includes: Item FOB + Proportional (Freight + Insurance + Handling) + BCD + SWS + IGST + CESS</p>
                  <p className="po-verification-checkmark">✓ Sum of all line item totals = {formatCurrency(totals.grandTotal)}</p>
                  <p className="po-verification-checkmark">✓ Calculation follows Indian Customs Act: BCD on CIF, IGST on Assessable Value (CIF + BCD + SWS)</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Delivery Schedule Card */}
        <div className="po-line-items-card">
          <div className="po-line-items-header">
            <div className="po-line-items-header-left">
              <h4 className="po-line-items-title">Delivery Schedule (Optional)</h4>
              <p className="po-line-items-subtitle">
                Specify multiple deliveries for items if required
              </p>
            </div>
            <button className="po-btn-add-line-item" onClick={handleAddDeliverySchedule} type="button">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-add-icon">
                <path d="M5 12h14"></path>
                <path d="M12 5v14"></path>
              </svg>
              Add Delivery Schedule
            </button>
          </div>
          <div className="po-line-items-content">
            <div className="po-table-wrapper">
              <table className="po-line-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Quantity</th>
                    <th>Delivery Date</th>
                    <th>Location</th>
                    <th className="po-table-col-action">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {deliverySchedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>
                        <select 
                          className="input-control input-control-select"
                          value={schedule.item || ''}
                          onChange={(e) => handleDeliveryScheduleItemChange(schedule.id, e.target.value)}
                        >
                          <option value="">Select Item</option>
                          {lineItems.map((item, idx) => (
                            <option key={item.id} value={item.id}>
                              Item {idx + 1} - {item.description} (Qty: {item.quantity} {getUnitLabel(item.uom)})
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input 
                          type="number" 
                          className="input-control input-control-number"
                          value={schedule.quantity}
                          onChange={(e) => setDeliverySchedules(prev => prev.map(s => 
                            s.id === schedule.id ? { ...s, quantity: parseFloat(e.target.value) || 0 } : s
                          ))}
                          min="0"
                          step="0.01"
                        />
                      </td>
                      <td>
                        <input 
                          type="date" 
                          className="input-control input-control-date"
                          value={schedule.deliveryDate}
                          onChange={(e) => setDeliverySchedules(prev => prev.map(s => 
                            s.id === schedule.id ? { ...s, deliveryDate: e.target.value } : s
                          ))}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </td>
                      <td>
                        <input 
                          type="text" 
                          className="input-control"
                          placeholder="Warehouse/Location"
                          value={schedule.location}
                          onChange={(e) => setDeliverySchedules(prev => prev.map(s => 
                            s.id === schedule.id ? { ...s, location: e.target.value } : s
                          ))}
                        />
                      </td>
                      <td>
                        <button 
                          className="po-btn-delete-item"
                          onClick={() => handleDeleteDeliverySchedule(schedule.id)}
                          type="button"
                          title="Remove schedule"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-delete-icon">
                            <path d="M10 11v6"></path>
                            <path d="M14 11v6"></path>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                            <path d="M3 6h18"></path>
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Terms & Conditions Card */}
        <div className="po-line-items-card">
          <div className="po-line-items-header">
            <div className="po-line-items-header-left">
              <h4 className="po-line-items-title">Terms & Conditions</h4>
              <p className="po-line-items-subtitle">
                Standard and custom terms applicable to this purchase order
              </p>
            </div>
            <button className="po-btn-add-line-item" onClick={handleAddTerm} type="button">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-add-icon">
                <path d="M5 12h14"></path>
                <path d="M12 5v14"></path>
              </svg>
              Add Term/Condition
            </button>
          </div>
          <div className="po-line-items-content">
            <div className="po-terms-list">
              {termsConditions.map((term, index) => (
                <div key={term.id} className="po-term-item">
                  <span className="po-term-number">{index + 1}.</span>
                  <textarea
                    className="po-term-textarea"
                    rows="1"
                    value={term.text}
                    onChange={(e) => handleTermChange(term.id, e.target.value)}
                    placeholder="Enter term/condition"
                  />
                  <button 
                    className="po-btn-delete-term"
                    onClick={() => handleDeleteTerm(term.id)}
                    type="button"
                    title="Remove term"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-delete-icon">
                      <path d="M10 11v6"></path>
                      <path d="M14 11v6"></path>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                      <path d="M3 6h18"></path>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Special Instructions Card */}
        <div className="po-line-items-card">
          <div className="po-line-items-header">
            <div className="po-line-items-header-left">
              <h4 className="po-line-items-title">Special Instructions</h4>
            </div>
          </div>
          <div className="po-line-items-content">
            <textarea
              className="po-special-instructions-textarea"
              value={specialInstructions}
              onChange={handleSpecialInstructionsChange}
              placeholder="Any special packaging, labeling, or delivery instructions..."
            />
          </div>
        </div>

        {/* Attachments Card */}
        <div className="po-line-items-card">
          <div className="po-line-items-header">
            <div className="po-line-items-header-left">
              <h4 className="po-line-items-title po-attachments-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-attachments-icon">
                  <path d="M12 3v12"></path>
                  <path d="m17 8-5-5-5 5"></path>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                </svg>
                Attachments
              </h4>
              <p className="po-line-items-subtitle">
                Attach relevant documents (RFQ, specifications, drawings)
              </p>
            </div>
            <label className="po-btn-add-line-item">
              <input 
                type="file" 
                multiple 
                onChange={handleFileUpload}
                className="po-file-input-hidden"
              />
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-add-icon">
                <path d="M12 3v12"></path>
                <path d="m17 8-5-5-5 5"></path>
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              </svg>
              Upload File
            </label>
          </div>
          <div className="po-line-items-content">
            <div className="po-attachments-list">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="po-attachment-item">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-attachment-icon">
                    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                    <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    <path d="M10 9H8"></path>
                    <path d="M16 13H8"></path>
                    <path d="M16 17H8"></path>
                  </svg>
                  <span className="po-attachment-name">{attachment.name}</span>
                  <button 
                    className="po-btn-delete-attachment"
                    onClick={() => handleDeleteAttachment(attachment.id)}
                    type="button"
                    title="Remove file"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-delete-icon">
                      <path d="M10 11v6"></path>
                      <path d="M14 11v6"></path>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                      <path d="M3 6h18"></path>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && (
        <div className="po-preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="po-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="po-preview-modal-header">
              <div className="po-preview-modal-header-left">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-icon">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                <div>
                  <h2 className="po-preview-modal-title">Preview Purchase Order</h2>
                  <p className="po-preview-modal-subtitle">Review all details before submitting</p>
                </div>
              </div>
              <button className="po-preview-modal-close" onClick={() => setShowPreviewModal(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="po-preview-modal-body">
              {/* Reference Quotation */}
              <div className="po-preview-section">
                <h3 className="po-preview-section-title">Reference Quotation</h3>
                <div className="po-preview-grid">
                  <div className="po-preview-item">
                    <label>Quotation ID</label>
                    <p>{quotationData.quotationId}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Linked RFQ</label>
                    <p>{quotationData.linkedRFQ}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Quote Date</label>
                    <p>{quotationData.quoteDate}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Valid Until</label>
                    <p>{quotationData.validUntil}</p>
                  </div>
                </div>
              </div>

              {/* PO Header Info */}
              <div className="po-preview-section">
                <h3 className="po-preview-section-title">Purchase Order Details</h3>
                <div className="po-preview-grid">
                  <div className="po-preview-item">
                    <label>PO Number</label>
                    <p>{formData.poNumber}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>PO Date</label>
                    <p>{formData.poDate}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Expected Delivery Date</label>
                    <p>{formData.expectedDeliveryDate}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Priority Level</label>
                    <p>{formData.priorityLevel}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Payment Terms</label>
                    <p>{getPaymentTermLabel(formData.paymentTerms)}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Currency</label>
                    <p>{(() => {
                      // Check if value is empty
                      if (!formData.currency || (typeof formData.currency === 'string' && formData.currency.trim() === '')) {
                        return 'Not specified';
                      }
                      // Find the label from currencyOptions if available
                      if (currencyOptions && currencyOptions.length > 0) {
                        const found = currencyOptions.find(opt => 
                          opt.currencyCode === formData.currency || 
                          opt.value === formData.currency
                        );
                        return found ? found.label : formData.currency;
                      }
                      return formData.currency;
                    })()}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Freight Terms</label>
                    <p>{(() => {
                      // Check if value is empty
                      if (!formData.freightTerms || (typeof formData.freightTerms === 'string' && formData.freightTerms.trim() === '')) {
                        return 'Not specified';
                      }
                      // Find the label from shippingTermsOptions if available
                      if (shippingTermsOptions && shippingTermsOptions.length > 0) {
                        const found = shippingTermsOptions.find(opt => 
                          opt.label === formData.freightTerms || 
                          opt.value === formData.freightTerms ||
                          (opt.label && opt.label.toLowerCase() === String(formData.freightTerms).toLowerCase())
                        );
                        return found ? found.label : formData.freightTerms;
                      }
                      return formData.freightTerms;
                    })()}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Delivery Terms</label>
                    <p>{getDeliveryTermLabel(formData.deliveryTerms)}</p>
                  </div>
                  <div className="po-preview-item">
                    <label>Trade Scenario</label>
                    <p>{formData.tradeScenario}</p>
                  </div>
                </div>
              </div>

              {/* International Trade Details */}
              {isInternationalTrade(formData.tradeScenario) && (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">International Trade Details</h3>
                  <div className="po-preview-grid">
                    <div className="po-preview-item">
                      <label>Port of Loading</label>
                      <p>{formData.portOfLoading || 'Not specified'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Port of Discharge</label>
                      <p>{formData.portOfDischarge || 'Not specified'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Customs Broker/CHA</label>
                      <p>{formData.customsBroker || 'Not specified'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>HS Code</label>
                      <p>{formData.hsCode || 'Not specified'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Country of Origin</label>
                      <p>{formData.countryOfOrigin || 'Not specified'}</p>
                    </div>
                    <div className="po-preview-item">
                      <label>Incoterms</label>
                      <p>{formData.incoterms || 'Not specified'}</p>
                    </div>
                  </div>
                  {internationalCharges && (
                    <div className="po-preview-grid po-preview-grid-margin-top">
                      <div className="po-preview-item">
                        <label>Shipping Charges</label>
                        <p>{formatCurrency(internationalCharges.shippingCharges)}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Insurance Charges</label>
                        <p>{formatCurrency(internationalCharges.insuranceCharges)}</p>
                      </div>
                      <div className="po-preview-item">
                        <label>Handling & Clearance Charges</label>
                        <p>{formatCurrency(internationalCharges.handlingCharges)}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Buyer & Seller Details */}
              <div className="po-preview-section">
                <h3 className="po-preview-section-title">Buyer & Seller Information</h3>
                <div className="po-preview-grid-2">
                  <div className="po-preview-card">
                    <h4 className="po-preview-card-title">Buyer Details</h4>
                    <div className="po-preview-details">
                      <p><strong>Company:</strong> {buyerDetails.companyName}</p>
                      <p><strong>Address:</strong> {formData.buyerAddress}</p>
                      <p><strong>GST:</strong> {formData.buyerGstNumber}</p>
                      <p><strong>State:</strong> {formData.buyerState}</p>
                      <p><strong>Country:</strong> {formData.buyerCountry}</p>
                    </div>
                  </div>
                  <div className="po-preview-card">
                    <h4 className="po-preview-card-title">Seller Details</h4>
                    <div className="po-preview-details">
                      <p><strong>Company:</strong> {sellerDetails.companyName}</p>
                      <p><strong>Address:</strong> {formData.sellerAddress}</p>
                      <p><strong>GST:</strong> {formData.sellerGstNumber}</p>
                      <p><strong>State:</strong> {formData.sellerState}</p>
                      <p><strong>Country:</strong> {formData.sellerCountry}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Line Items */}
              <div className="po-preview-section">
                <h3 className="po-preview-section-title">Line Items</h3>
                <div className="po-preview-table-wrapper">
                  <table className="po-preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>HSN</th>
                        <th>Qty</th>
                        <th>UOM</th>
                        <th>Unit Price</th>
                        <th>Disc %</th>
                        {isInternationalTrade(formData.tradeScenario) ? (
                          <>
                            <th>Customs %</th>
                            <th>IGST %</th>
                            <th>CESS %</th>
                          </>
                        ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                          <>
                            <th>IGST %</th>
                            <th>CESS %</th>
                          </>
                        ) : (
                          <>
                            <th>CGST %</th>
                            <th>SGST %</th>
                            <th>CESS %</th>
                          </>
                        )}
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.description}</td>
                          <td>{item.hsn}</td>
                          <td>{item.quantity}</td>
                          <td>{getUnitLabel(item.uom)}</td>
                          <td>{formatCurrency(item.unitPrice)}</td>
                          <td>{item.discount}%</td>
                          {isInternationalTrade(formData.tradeScenario) ? (
                            <>
                              <td>{item.customsDuty || 0}%</td>
                              <td>{item.igst}%</td>
                              <td>{item.cess}%</td>
                            </>
                          ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                            <>
                              <td>{item.igst}%</td>
                              <td>{item.cess}%</td>
                            </>
                          ) : (
                            <>
                              <td>{item.cgst}%</td>
                              <td>{item.sgst}%</td>
                              <td>{item.cess}%</td>
                            </>
                          )}
                          <td className="po-table-col-total-cell">{formatCurrency(item.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      {formData.tradeScenario !== '→IN Import to India (Abroad → India)' && (
                        <tr className="po-preview-total-row">
                          <td colSpan={isInternationalTrade(formData.tradeScenario) ? "10" : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label">Subtotal (Qty × Unit Price):</td>
                          <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.subtotal)}</span></td>
                        </tr>
                      )}
                          {isInternationalTrade(formData.tradeScenario) ? (
                        <>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Basic Customs Duty (BCD):</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.basicCustomsDuty || 0)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Social Welfare Surcharge (10% of BCD):</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.socialWelfareSurcharge || 0)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Assessable Value (CIF + BCD + SWS):</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.assessableValue || 0)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">IGST (on Assessable Value):</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.igstOnAssessable || 0)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Shipping Charges:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(internationalCharges.shippingCharges)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Insurance Charges:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(internationalCharges.insuranceCharges)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">Handling & Clearance Charges:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(internationalCharges.handlingCharges)}</span></td>
                          </tr>
                        </>
                      ) : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? (
                        <>
                          <tr className="po-preview-total-row">
                            <td colSpan="9" className="po-preview-summary-label">IGST:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.igst)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="9" className="po-preview-summary-label">CESS:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                          </tr>
                        </>
                      ) : (
                        <>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">CGST:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cgst)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">SGST:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.sgst)}</span></td>
                          </tr>
                          <tr className="po-preview-total-row">
                            <td colSpan="10" className="po-preview-summary-label">CESS:</td>
                            <td className="po-preview-summary-value"><span className="font-semibold">{formatCurrency(totals.cess)}</span></td>
                          </tr>
                        </>
                      )}
                      <tr className="po-preview-total-row po-preview-grand-total">
                        <td colSpan={isInternationalTrade(formData.tradeScenario) ? "10" : formData.tradeScenario === 'IN Inter-State (Between Indian States)' ? "9" : "10"} className="po-preview-summary-label">
                          <span className="font-semibold">Grand Total (Sum of Line Items):</span>
                        </td>
                        <td className="po-preview-summary-value">
                          <span className="font-semibold">{formatCurrency(totals.grandTotal)}</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Delivery Schedule */}
              {deliverySchedules.length > 0 && (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Delivery Schedule</h3>
                  <div className="po-preview-table-wrapper">
                    <table className="po-preview-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Quantity</th>
                          <th>Delivery Date</th>
                          <th>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deliverySchedules.map((schedule) => {
                          const item = lineItems.find(i => i.id.toString() === schedule.item.toString());
                          return (
                            <tr key={schedule.id}>
                              <td>{item ? item.description : `Item ${schedule.item}`}</td>
                              <td>{schedule.quantity}</td>
                              <td>{schedule.deliveryDate}</td>
                              <td>{schedule.location}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Terms & Conditions */}
              {termsConditions.length > 0 && (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Terms & Conditions</h3>
                  <ol className="po-preview-terms-list">
                    {termsConditions.map((term, index) => (
                      <li key={term.id}>{term.text}</li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Special Instructions */}
              {specialInstructions && (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Special Instructions</h3>
                  <p className="po-preview-text">{specialInstructions}</p>
                </div>
              )}

              {/* Attachments */}
              {attachments.length > 0 && (
                <div className="po-preview-section">
                  <h3 className="po-preview-section-title">Attachments</h3>
                  <div className="po-preview-attachments-list">
                    {attachments.map((attachment) => (
                      <div key={attachment.id} className="po-preview-attachment-item">
                        <div className="po-preview-attachment-info">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="po-preview-attachment-icon">
                            <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                            <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                            <path d="M10 9H8"></path>
                            <path d="M16 13H8"></path>
                            <path d="M16 17H8"></path>
                          </svg>
                          <span className="po-preview-attachment-name">{attachment.name}</span>
                        </div>
                        <button 
                          className="po-preview-view-document-btn"
                          onClick={() => {
                            // Open document in new tab (you can replace with actual document URL)
                            window.open(`/documents/${attachment.name}`, '_blank');
                          }}
                          title="View Document"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          View Document
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="po-preview-modal-footer">
              <button className="po-btn-secondary" onClick={() => setShowPreviewModal(false)}>
                Close
              </button>
              <button className="po-btn-primary" onClick={() => {
                setShowPreviewModal(false);
                handleSubmit();
              }}>
                Submit Purchase Order
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

